// Liste centralisée des modules disponibles pour tous les formulaires.
// appliesTo détermine quels modules sont proposés selon le type de client
// (Restauration/Hôtellerie vs Prestation) — cf. docs/PROJET_STATE.md.
export type ClientType = 'restauration' | 'prestation';
export const ALL_MODULES: { name: string; label: string; appliesTo: ClientType | 'both' }[] = [
    { name: 'planning', label: 'Planning', appliesTo: 'restauration' },
    { name: 'evenementiel', label: 'Événementiel', appliesTo: 'restauration' },
    { name: 'exception_service', label: "Service d'exception", appliesTo: 'restauration' },
    // Détachés d'« Événementiel » (2026-08-25, demande d'Emeni) : un traiteur peut vouloir gérer ses
    // privatisations sans ouvrir la collecte des choix de menu, ou l'inverse.
    { name: 'menus_convives', label: 'Menus & Convives', appliesTo: 'restauration' },
    { name: 'menu_plats', label: 'Menu & Plats', appliesTo: 'restauration' },
    // Le service du jour J, cote cuisine. Module a part et non simple ecran de "Menus & Convives"
    // (choix d'Emeni, 2026-08-26) : une brigade doit pouvoir suivre le service sans recevoir au passage
    // toute la configuration commerciale des menus. Cote serveur, seules live-checkins et prep-toggle lui
    // sont ouvertes.
    { name: 'mode_terrain', label: 'Mode terrain', appliesTo: 'restauration' },
    // Le pipeline commercial de la privatisation, de la demande client jusqu'a la cloture. Distinct
    // d'« Evenementiel », qui reste le calendrier et la fiche : ici c'est le suivi de l'affaire.
    { name: 'suivi_privatisation', label: 'Suivi privatisation', appliesTo: 'restauration' },
    { name: 'rdv_pro', label: 'RDV Pro', appliesTo: 'restauration' },
    { name: 'planning_rdv', label: 'Planning (rendez-vous)', appliesTo: 'prestation' },
    { name: 'prestations', label: 'Prestations', appliesTo: 'prestation' },
    { name: 'facture', label: 'Facture', appliesTo: 'both' },
    { name: 'crm', label: 'CRM', appliesTo: 'both' },
    { name: 'employes', label: 'Postes & Employés', appliesTo: 'both' },
    { name: 'portail_salarie', label: 'Portail salarié', appliesTo: 'both' },
    { name: 'demande_conges', label: 'Demande de congé(s)', appliesTo: 'both' },
    { name: 'todo', label: 'ToDo', appliesTo: 'both' },
    { name: 'notes', label: 'Notes', appliesTo: 'both' },
    { name: 'fournisseurs', label: 'Fournisseurs', appliesTo: 'both' },
    { name: 'commandes', label: 'Commandes', appliesTo: 'both' },
    { name: 'extras_partenaires', label: 'Partenaires Extras', appliesTo: 'both' },
    { name: 'extras', label: 'Extras', appliesTo: 'both' },
];

export const getModulesForClientType = (clientType?: string | null) => {
    const type: ClientType = clientType === 'prestation' ? 'prestation' : 'restauration';
    return ALL_MODULES.filter((m) => m.appliesTo === 'both' || m.appliesTo === type);
};

const ACTIONS_TRANSLATION = {
    'PRINT_INVOICE': 'Impression Facture',
    'MODULE_VISIT': 'Navigation Module',
    'PRINT_DOCUMENT': 'Impression Document',
    'DOWNLOAD_DOCUMENT': 'Téléchargement Document'
};

import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LegalMentions } from './components/legal/LegalMentions';
import { PrivacyPolicy } from './components/legal/PrivacyPolicy';
import { LegalLayout } from './components/legal/LegalLayout';
import { 
    LayoutDashboard,
    Users,
    Calendar,
    Briefcase,
    Zap,
    FileText, 
    Settings, 
    LogOut, 
    Plus, 
    CheckCircle, 
    XCircle,
    ChevronRight,
    ShieldCheck,
    ShieldAlert,
    Lock,
    Mail,
    Trash2,
    ToggleLeft,
    ToggleRight,
    Search,
    Filter,
    Download,
    MapPin,
    UserCheck,
    Palette,
    Building2,
    Phone,
    MessageSquare,
    Paperclip,
    FileSpreadsheet,
    Upload,
    AlertCircle,
    CheckCircle2,
    X,
    Sun,
    Moon,
    Save,
    FileJson,
    Clock,
    UtensilsCrossed,
    ChefHat,
    Utensils,
    UserPlus,
    Info,
    Package,
    CalendarClock,
    CalendarCheck,
    ClipboardList,
    Send,
    ListChecks,
    Receipt,
    ChevronDown,
    NotebookText,
    Truck,
    HardHat
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { authApi, adminApi, moduleApi, supportApi, dashboardApi, planningApi, apiFetch, publicRdvProBookingApi } from './lib/api';
import { extractRowsFromPdfClient, extractEvenementielRowsFromPdfClient } from './lib/pdfExtractor';
import { resolveLogoUrl } from './lib/resolveLogoUrl';
import { resizeImageForUpload } from './lib/resizeImageForUpload';
import { EvenementielModule } from './components/Evenementiel/EvenementielModule';
import { EvenementielConvivesPage } from './components/Evenementiel/convives/EvenementielConvivesPage';
import { MenusConvivesListPage } from './components/Evenementiel/convives/MenusConvivesListPage';
import { SuiviPrivatisationModule } from './components/SuiviPrivatisation/SuiviPrivatisationModule';
import { SuiviPrivatisationLayout } from './components/SuiviPrivatisation/SuiviPrivatisationLayout';
import { DossierPipelinePage } from './components/SuiviPrivatisation/DossierPipelinePage';
import { DossierBriefPage } from './components/SuiviPrivatisation/DossierBriefPage';
import { DossierDocumentsPage } from './components/SuiviPrivatisation/DossierDocumentsPage';
import { DossierMenusPage } from './components/SuiviPrivatisation/DossierMenusPage';
import { DossierJournalPage } from './components/SuiviPrivatisation/DossierJournalPage';
import { MenuCatalogPage } from './components/Evenementiel/menu/MenuCatalogPage';
import { AllergenFamiliesPage } from './components/Evenementiel/menu/AllergenFamiliesPage';
import { ConvivesSuiviPage } from './components/Evenementiel/convives/ConvivesSuiviPage';
import ModeTerrainPage from './components/Evenementiel/convives/ModeTerrainPage';
import ModeTerrainListPage from './components/Evenementiel/convives/ModeTerrainListPage';
import { QrCodesPage } from './components/Evenementiel/convives/QrCodesPage';
import { RdvProModule } from './components/RdvPro/RdvProModule';
import { RdvCancellationHistoryPage } from './components/RdvPro/RdvCancellationHistoryPage';
import { SendDocumentByEmailModal } from './components/RdvPro/SendDocumentByEmailModal';
import { PlanningModule } from './components/Planning/PlanningModule';
import { RoadmapPage } from './components/Planning/Roadmap/RoadmapPage';
import { FacturesModule } from './components/Factures/FacturesModule';
import { CRMModule } from './components/CRM/CRMModule';
import { TodoModule } from './components/Todo/TodoModule';
import { FloatingTodoWidget } from './components/Todo/FloatingTodoWidget';
import { NotesModule } from './components/Notes/NotesModule';
import { FournisseursModule } from './components/Fournisseurs/FournisseursModule';
import { SupplierDetailPage } from './components/Fournisseurs/SupplierDetailPage';
import { SupplierOrdersPage } from './components/Fournisseurs/SupplierOrdersPage';
import { SupplierArticlesPage } from './components/Fournisseurs/SupplierArticlesPage';
import { SupplierCategoryArticlesPage } from './components/Fournisseurs/SupplierCategoryArticlesPage';
import { CommandesModule } from './components/Fournisseurs/CommandesModule';
import { ExtrasPartnersModule } from './components/Extras/ExtrasPartnersModule';
import { ExtrasPartnerDetailPage } from './components/Extras/ExtrasPartnerDetailPage';
import { ExtrasPartnerTypesPage } from './components/Extras/ExtrasPartnerTypesPage';
import { ExtrasPartnerCategoryTypesPage } from './components/Extras/ExtrasPartnerCategoryTypesPage';
import { ExtrasPartnerRequestsPage } from './components/Extras/ExtrasPartnerRequestsPage';
import { ExtrasModule } from './components/Extras/ExtrasModule';
import { AdminEvenementielConfig } from './components/Admin/AdminEvenementielConfig';
import { AdminPlanningConfig } from './components/Admin/AdminPlanningConfig';
import AdminSettings from './components/Admin/AdminSettings';
import { AdminCollaboratorRequests } from './components/Admin/AdminCollaboratorRequests';
import { AdminExceptionServiceAutomationRequests } from './components/Admin/AdminExceptionServiceAutomationRequests';
import { SendRegistrationModal } from './components/Admin/SendRegistrationModal';
import { RegistrationRequestsView } from './components/Admin/RegistrationRequestsView';
import { ClientRegistrationForm } from './components/Public/ClientRegistrationForm';
import { BookingWidget } from './components/Public/BookingWidget';
import { RdvProBookingWidget } from './components/Public/RdvProBookingWidget';
import { DocumentationResponse } from './components/Public/DocumentationResponse';
import { EvenementielGuestPage } from './components/Public/EvenementielGuestPage';
import { TableCheckinPage } from './components/Public/TableCheckinPage';
import { SupplierOrderConfirmPage } from './components/Public/SupplierOrderConfirmPage';
import { RdvCancellationPage } from './components/Public/RdvCancellationPage';
import { ExtrasRequestConfirmPage } from './components/Public/ExtrasRequestConfirmPage';
import { DevisSignaturePage } from './components/Public/DevisSignaturePage';
import { PageNumberBadge } from './components/ui/PageNumberBadge';
import { PrestationsModule } from './components/Prestations/PrestationsModule';
import { PrestationPlanningModule } from './components/PrestationPlanning/PrestationPlanningModule';
import { PrestationEmployeeDashboard } from './components/Employee/PrestationEmployeeDashboard';
import { SettingsPage } from './components/Settings/SettingsPage';
import { PostesEmployesModule } from './components/Employes/PostesEmployesModule';
import { EmployeeProfilePage } from './components/Employes/EmployeeProfilePage';
import { ExceptionServiceModule } from './components/ExceptionService/ExceptionServiceModule';
import { PlanningTableView } from './components/Planning/PlanningTableView';
import { SupportModal } from './components/Support/SupportModal';
import { InfoModal } from './components/InfoModal';
import { useTheme } from './hooks/useTheme';
import { SentinelJournal } from './components/Admin/SentinelJournal';
import { RHModule } from './components/RH/RHModule';
import { EmployeeAbsences } from './components/Employee/EmployeeAbsences';
import { EmployeeSurveys } from './components/Employee/EmployeeSurveys';
import { EmployeeOnboarding } from './components/Employee/EmployeeOnboarding';
import { EmployeeExpenses } from './components/Employee/EmployeeExpenses';
import { EmployeeTimeClock } from './components/Employee/EmployeeTimeClock';
import { EmployeeMessages } from './components/Employee/EmployeeMessages';
import { PointageKiosque } from './components/Kiosque/PointageKiosque';
import { TeamMessagingPage } from './components/Employes/TeamMessagingPage';
import { NotesDeFraisPage } from './components/Employes/NotesDeFraisPage';
import { PointageManagement } from './components/Employes/PointageManagement';
import { PublicHomepage } from './components/Public/PublicHomepage';
import { DemandesRecurrentesPage } from './components/Demandes/DemandesRecurrentesPage';
import { CRMContactDemarchesPage } from './components/CRM/CRMContactDemarchesPage';
import { CRMContactFacturesPage } from './components/CRM/CRMContactFacturesPage';
import { CRMContactCommandesPage } from './components/CRM/CRMContactCommandesPage';
import { CRMContactHistoriquePage } from './components/CRM/CRMContactHistoriquePage';

const SidebarItem = ({ icon: Icon, label, to, active, collapsed = false, badge }: any) => (
    <Link
        to={to}
        className={`group flex items-center ${collapsed ? 'justify-center' : 'gap-2'} ${collapsed ? 'px-0 py-1' : 'pl-2.5 pr-3 py-1.5'} rounded-lg transition-all relative ${
            active
                ? 'bg-brand-500/10 text-brand-700 dark:text-brand-300'
                : 'text-[var(--text-secondary)] hover:bg-brand-600/10 hover:text-brand-600 dark:hover:text-brand-400'
        }`}
        title={collapsed ? label : undefined}
    >
        {active && !collapsed && (
            <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full bg-brand-500" />
        )}
        <span className={`inline-flex items-center justify-center ${collapsed ? 'w-8 h-8 rounded-lg group-hover:bg-[var(--interactive-hover)]' : 'w-5 h-5'} ${active && collapsed ? 'bg-brand-500/15 rounded-lg' : ''} transition-all relative`}>
            <Icon className="w-[18px] h-[18px] shrink-0" />
            {collapsed && badge !== undefined && badge > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-content text-[9px] font-black flex items-center justify-center shadow-lg shadow-red-500/40 animate-pulse">
                    {badge > 99 ? '99+' : badge}
                </span>
            )}
        </span>
        {!collapsed && <span className={`text-[14px] min-w-0 truncate flex-1 ${active ? 'font-semibold' : 'font-medium'}`}>{label}</span>}
        {!collapsed && badge !== undefined && badge > 0 && (
            <span className="min-w-[20px] h-[20px] px-1.5 rounded-full bg-red-500 text-content text-[10px] font-black flex items-center justify-center shadow-lg shadow-red-500/30 animate-pulse">
                {badge > 99 ? '99+' : badge}
            </span>
        )}
    </Link>
);

// Groupe dépliable de SidebarItem (ex: "RH & équipe" regroupant Postes & Employés + Demande de congé(s)) —
// replié par défaut sauf si la page actuellement affichée fait partie du groupe (jamais "perdu" en arrivant
// sur une page dont la catégorie serait repliée). Quand la sidebar entière est réduite à la barre d'icônes
// (collapsed=true), le regroupement n'a plus de sens visuel : on retombe simplement sur la liste à plat.
const SidebarCategory = ({ icon: Icon, label, collapsed = false, defaultOpen = false, badge, children }: any) => {
    const [open, setOpen] = useState(defaultOpen);
    if (collapsed) return <>{children}</>;
    return (
        <div>
            <button
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center gap-2 pl-2.5 pr-3 py-1.5 rounded-lg transition-all text-[var(--text-secondary)] hover:bg-brand-600/10 hover:text-brand-600 dark:hover:text-brand-400"
            >
                <span className="inline-flex items-center justify-center w-5 h-5"><Icon className="w-[18px] h-[18px] shrink-0" /></span>
                <span className="text-[13px] min-w-0 truncate flex-1 text-left font-bold uppercase tracking-wide">{label}</span>
                {badge !== undefined && badge > 0 && (
                    <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-content text-[9px] font-black flex items-center justify-center shrink-0">
                        {badge > 99 ? '99+' : badge}
                    </span>
                )}
                <ChevronDown size={14} className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
            {open && (
                <div className="ml-3 pl-2.5 border-l border-[var(--border-color)] space-y-0.5 mt-0.5 mb-0.5">
                    {children}
                </div>
            )}
        </div>
    );
};

const ModuleTracker = () => {
    const location = useLocation();
    const { user } = useAuth();
    const lastPathRef = React.useRef(location.pathname);

    React.useEffect(() => {
        if (!user || user.type === 'admin') return;
        
        const path = location.pathname;
        if (path === lastPathRef.current) return;
        
        const modules: Record<string, string> = {
            '/planning': 'Planning',
            '/evenementiel': 'Événementiel',
            '/factures': 'Facturation',
            '/crm': 'CRM',
            '/employes': 'Gestion Staff',
            '/demandes-conges': 'Demande de congé(s)'
        };
        
        const moduleName = modules[path as keyof typeof modules];
        if (moduleName) {
            adminApi.logAction({
                action: 'MODULE_VISIT',
                category: 'METIER',
                message: `A consulté le module : ${moduleName}`
            }).catch(() => {});
        }
        
        lastPathRef.current = path;
    }, [location.pathname, user]);

    return null;
};

const Layout = ({ children }: any) => {
    const { user, logout, refreshUser, loading } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const navigate = useNavigate();
    const location = useLocation();

    React.useEffect(() => {
        if (!loading) {
            if (!user) {
                // Garde l'URL demandée (ex: un lien de booking d'extras avec ?bookEvent=...) pour y revenir
                // après connexion, au lieu de toujours retomber sur le tableau de bord par défaut — demande
                // d'Emeni (2026-08-22) : "la personne à qui j'envoie le lien doit être connectée pour accéder
                // à la privat concernée pour booker".
                navigate('/login', { state: { from: `${location.pathname}${location.search}` } });
            } else if (user.type === 'employe' && window.location.pathname !== '/planning' && window.location.pathname !== '/absences' && window.location.pathname !== '/parametres' && window.location.pathname !== '/mon-tableau-de-bord' && window.location.pathname !== '/mes-sondages' && window.location.pathname !== '/mon-integration' && window.location.pathname !== '/mes-notes-de-frais' && window.location.pathname !== '/pointage' && window.location.pathname !== '/mes-messages') {
                navigate('/planning');
            }
        }
    }, [user, loading, navigate]);

    const [activeModules, setActiveModules] = React.useState<string[]>([]);
    const [isCollapsed, setIsCollapsed] = React.useState(() => {
        try {
            const saved = localStorage.getItem('sidebarCollapsed');
            return saved === '1';
        } catch {
            return false;
        }
    });
    const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
    // Referme le menu burger mobile après un clic sur un lien de la sidebar — sans ça, le menu (et son
    // overlay sombre plein écran) restait ouvert par-dessus la page de destination, donnant l'impression que
    // la navigation avait échoué ou que l'appli était bloquée sur mobile. Demande d'Emeni (2026-08-22, audit
    // responsive mobile/tablette).
    React.useEffect(() => { setIsMobileMenuOpen(false); }, [location.pathname]);
    const [supportUnreadCount, setSupportUnreadCount] = React.useState(0);
    const [showSupportModal, setShowSupportModal] = React.useState(false);
    const [clientSupportUnreadCount, setClientSupportUnreadCount] = React.useState(0);
    const [pendingAbsenceCount, setPendingAbsenceCount] = React.useState(0);
    const [pendingCollabRequestCount, setPendingCollabRequestCount] = React.useState(0);
    const [pendingBookingRequestCount, setPendingBookingRequestCount] = React.useState(0);
    const [nextPendingBookingRequest, setNextPendingBookingRequest] = React.useState<{ id: string; date: string } | null>(null);
    const [myMessagesUnreadCount, setMyMessagesUnreadCount] = React.useState(0);
    const [teamMessagesUnreadCount, setTeamMessagesUnreadCount] = React.useState(0);
    const [pendingExpenseCount, setPendingExpenseCount] = React.useState(0);

    React.useEffect(() => {
        try {
            localStorage.setItem('sidebarCollapsed', isCollapsed ? '1' : '0');
        } catch {}
    }, [isCollapsed]);

    const resolvedLogoUrl = resolveLogoUrl(user?.logoUrl);

    const loadModules = React.useCallback(async () => {
        try {
            const modules = await authApi.getMyModules();
            setActiveModules(modules.filter((m: any) => m.is_active === 1).map((m: any) => m.module_name));
        } catch (e) {
            console.error('Failed to load active modules', e);
        }
    }, []);

    // Ne déconnecte QUE sur un refus explicite du serveur (401/403) — même règle que verifyToken dans
    // AuthContext. Auparavant le catch était aveugle : n'importe quelle erreur réseau déconnectait, et comme
    // apiFetch abandonne une requête au bout de 10 s sans jamais retenter un AbortError, une 4G lente ou une
    // requête gelée par iOS (Safari suspend les timers quand le Trousseau passe au premier plan pendant la
    // connexion) renvoyait l'utilisateur sur /login juste après une connexion pourtant réussie. Signalé par
    // Emeni (2026-08-23) : impossible de se connecter depuis un téléphone.
    const checkStatus = React.useCallback(async () => {
        try {
            const me = await authApi.getMe();
            if (me.status === 'blocked') {
                logout();
                navigate('/login');
            }
        } catch (e: any) {
            const isAuthRejection = e?.status === 401 || e?.status === 403
                || (e?.message && /401|403|Forbidden|Unauthorized|Session expirée/.test(e.message));
            if (isAuthRejection) {
                logout();
                navigate('/login');
            } else {
                console.warn('checkStatus: erreur réseau ignorée, session conservée', e);
            }
        }
    }, [logout, navigate]);

    React.useEffect(() => {
        if (user && user.type !== 'admin') {
            loadModules();
            checkStatus();
        }
    }, [user, loadModules, checkStatus]);

    const refreshClientUnreadCount = React.useCallback(async () => {
        if (user && user.type !== 'admin') {
            try {
                const data = await supportApi.getClientUnreadCount();
                setClientSupportUnreadCount(Number(data?.count || 0));
            } catch {}
        }
    }, [user]);

    React.useEffect(() => {
        if (!user) return;
        let mounted = true;
        const controller = new AbortController();

        const loadUnread = async () => {
            try {
                if (user.type === 'admin') {
                    const data = await supportApi.getAdminUnreadCount();
                    if (mounted) setSupportUnreadCount(Number(data?.count || 0));
                    try {
                        const reqs = await adminApi.getCollaboratorRequests();
                        if (Array.isArray(reqs)) {
                            const count = reqs.filter((r: any) => r.status === 'pending').length;
                            if (mounted) setPendingCollabRequestCount(count);
                        }
                    } catch {}
                } else {
                    const data = await supportApi.getClientUnreadCount();
                    if (mounted) setClientSupportUnreadCount(Number(data?.count || 0));

                    if (user.type !== 'employe' && activeModules.includes('demande_conges')) {
                        try {
                            const token = localStorage.getItem('token');
                            const reqs = await fetch('/api/absence-requests', {
                                headers: { Authorization: `Bearer ${token}` },
                                signal: controller.signal
                            }).then(r => r.json());
                            if (Array.isArray(reqs)) {
                                const count = reqs.filter((r: any) => r.status === 'pending').length;
                                if (mounted) setPendingAbsenceCount(count);
                            }
                        } catch {}
                    } else if (user.type === 'employe') {
                        try {
                            const data: any = await moduleApi.getMyAnnouncementsUnreadCount();
                            if (mounted) setMyMessagesUnreadCount(Number(data?.count || 0));
                        } catch {}
                    }
                }
            } catch (e: any) {
                // Silently ignore AbortError — it's expected on cleanup/navigation
                if (e?.name === 'AbortError' || e instanceof DOMException) return;
            }
        };
        loadUnread();
        // 60s interval — checking every minute is more than sufficient for unread count
        const timer = setInterval(loadUnread, 60000);

        // Immediate refresh when an action occurs (e.g. accepted/refused/submitted)
        window.addEventListener('absenceRequestProcessed', loadUnread);
        window.addEventListener('collaboratorRequestProcessed', loadUnread);

        return () => {
            mounted = false;
            controller.abort();
            clearInterval(timer);
            window.removeEventListener('absenceRequestProcessed', loadUnread);
            window.removeEventListener('collaboratorRequestProcessed', loadUnread);
        };
    }, [user, activeModules]);

    // Bulle rouge sur l'entrée "Planning" (client Prestation) : nombre de demandes de réservation en ligne
    // en attente de validation (widget public /reserver/:clientId).
    React.useEffect(() => {
        if (!user || user.type === 'admin' || user.type === 'employe' || user.client_type !== 'prestation' || !activeModules.includes('planning_rdv')) return;
        let mounted = true;
        const loadPendingBookingCount = async () => {
            try {
                const data = await moduleApi.getPrestationPendingRequestsCount();
                if (mounted) {
                    setPendingBookingRequestCount(Number(data?.count || 0));
                    setNextPendingBookingRequest(data?.next_id && data?.next_date ? { id: data.next_id, date: data.next_date } : null);
                }
            } catch {}
        };
        loadPendingBookingCount();
        const timer = setInterval(loadPendingBookingCount, 60000);
        window.addEventListener('prestationBookingRequestProcessed', loadPendingBookingCount);
        return () => {
            mounted = false;
            clearInterval(timer);
            window.removeEventListener('prestationBookingRequestProcessed', loadPendingBookingCount);
        };
    }, [user, activeModules]);

    // Bulles rouges sur la nouvelle entrée sidebar "Messagerie" (messages d'équipe non lus + notes de
    // frais en attente) — même principe que pendingAbsenceCount, mais regroupées pour cette catégorie.
    React.useEffect(() => {
        if (!user || user.type === 'admin' || user.type === 'employe' || !activeModules.includes('portail_salarie')) return;
        let mounted = true;
        const loadMessagerieCounts = async () => {
            try {
                const data: any = await moduleApi.getTeamAnnouncementsUnreadCount();
                if (mounted) setTeamMessagesUnreadCount(Number(data?.count || 0));
            } catch {}
            try {
                const reports = await moduleApi.getExpenseReports();
                if (mounted && Array.isArray(reports)) setPendingExpenseCount(reports.filter((r: any) => r.status === 'pending').length);
            } catch {}
        };
        loadMessagerieCounts();
        const timer = setInterval(loadMessagerieCounts, 60000);
        return () => {
            mounted = false;
            clearInterval(timer);
        };
    }, [user, activeModules]);

    if (loading) return null;
    if (!user) return null;

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const isModuleActive = (name: string) => {
        if (!user) return false;
        if (user.type === 'admin') return false; // Admin uses a separate hardcoded section
        
        // 1. Check live modules fetched from server (most accurate)
        if (activeModules.length > 0) {
            return activeModules.includes(name);
        }

        // 2. Fallback to JWT permissions (immediate display during loading)
        const jwtModules = (user as any).modules || user.modules_access || [];
        return jwtModules.includes(name);
    };

    return (
        <div
            className="flex min-h-screen bg-[var(--bg-app)] text-[var(--text-primary)] transition-colors duration-300"
            style={{ '--sidebar-w': isCollapsed ? '72px' : '248px' } as React.CSSProperties}
        >
            {user && user.type !== 'admin' && <ModuleTracker />}
            
            {/* Overlay Mobile */}
            {isMobileMenuOpen && (
                <div 
                    className="fixed inset-0 bg-black/60 z-40 md:hidden" 
                    onClick={() => setIsMobileMenuOpen(false)}
                />
            )}

            {/* Sidebar */}
            <motion.aside
                animate={{ width: isCollapsed ? 72 : 248 }}
                transition={{ duration: 0.25, ease: 'easeInOut' }}
                // min-w-0 + overflow-hidden : sans eux, la règle flex `min-width:auto` donne à la barre la
                // largeur minimale de son CONTENU, et le nom de l'établissement affiché en gros caractères
                // l'emportait sur le width animé ci-dessus — « Le Petit Cageot SARL » l'étirait à 464px au
                // lieu de 248, volant plus de 200px à toutes les pages sur un écran d'ordinateur. Signalé par
                // Emeni (2026-08-24) : "certaines pages ne prennent pas toute la page en version PC".
                className={`bg-[var(--bg-sidebar)] border-r border-[var(--border-color)] p-3 md:p-4 flex flex-col h-screen fixed md:sticky top-0 z-50 min-w-0 overflow-hidden transition-transform duration-300 md:translate-x-0 ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}
            >
                <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} mb-2 shrink-0`}>
                    <div className={`flex items-center ${isCollapsed ? '' : 'gap-2'} px-2`}>
                        <div className="w-7 h-7 bg-white rounded-lg flex items-center justify-center overflow-hidden">
                            {resolvedLogoUrl ? (
                                <img
                                    src={resolvedLogoUrl}
                                    alt={user?.company_name || 'Logo client'}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                        (e.currentTarget as HTMLImageElement).style.display = 'none';
                                    }}
                                />
                            ) : (
                                <ShieldCheck className="text-content" size={20} />
                            )}
                        </div>
                        {!isCollapsed && (
                            user?.type === 'employe' ? (
                                <div className="flex flex-col justify-center ml-1">
                                    <span className="font-black text-[15px] leading-none text-content">{user?.name}</span>
                                    <span className="text-[10px] text-content-muted font-bold uppercase tracking-wider mt-0.5">{user?.company_name}</span>
                                </div>
                            ) : (
                                // Taille dégressive selon la longueur du nom : 36px convient à « L'IAmani »,
                                // pas à « Le Petit Cageot SARL » qui débordait de la barre. Le nom reste
                                // affiché en entier (retour à la ligne), simplement dans une taille qui tient
                                // dans les 248px de la barre.
                                <span
                                    className="font-bold leading-none tracking-tight text-slate-700 dark:text-[var(--text-primary)] break-words min-w-0"
                                    style={{ fontSize: (() => {
                                        const n = (user?.company_name || "L'IAmani").length;
                                        return n <= 10 ? 32 : n <= 16 ? 26 : n <= 24 ? 21 : 18;
                                    })() }}
                                >{user?.company_name || "L'IAmani"}</span>
                            )
                        )}
                    </div>
                    {!isCollapsed && (
                        <button
                            onClick={() => setIsCollapsed((prev) => !prev)}
                            className="p-1.5 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--interactive-hover)] transition-all"
                            aria-label="Réduire la barre latérale"
                        >
                            <ChevronRight className={`${isCollapsed ? 'rotate-180' : ''} transition-transform`} size={16} />
                        </button>
                    )}
                </div>

                {isCollapsed && (
                    <button
                        onClick={() => setIsCollapsed((prev) => !prev)}
                        className="mb-3 p-1.5 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--interactive-hover)] transition-all mx-auto"
                        aria-label="Étendre la barre latérale"
                    >
                        <ChevronRight className={`${isCollapsed ? 'rotate-180' : ''} transition-transform`} size={16} />
                    </button>
                )}

                <nav className="flex-1 space-y-0.5 overflow-y-auto min-h-0 sidebar-nav-scroll">
                    {user?.type !== 'employe' && (
                        <SidebarItem icon={LayoutDashboard} label="Dashboard" to="/" active={window.location.pathname === '/'} collapsed={isCollapsed} />
                    )}
                    
                    {user?.type === 'employe' ? (
                        <>
                            {!isCollapsed && <div className="mt-3 mb-1 px-3 text-[9px] font-bold text-slate-600 dark:text-content-muted uppercase tracking-widest">Espace Salarié</div>}
                            {user?.client_type === 'prestation' && isModuleActive('portail_salarie') && <SidebarItem icon={LayoutDashboard} label="Dashboard" to="/mon-tableau-de-bord" active={window.location.pathname === '/mon-tableau-de-bord'} collapsed={isCollapsed} />}
                            {isModuleActive('portail_salarie') && <SidebarItem icon={Calendar} label="Mon Planning" to="/planning" active={window.location.pathname === '/planning'} collapsed={isCollapsed} />}
                            {isModuleActive('demande_conges') && <SidebarItem icon={Clock} label="Mes Demandes" to="/absences" active={window.location.pathname === '/absences'} collapsed={isCollapsed} />}
                            {isModuleActive('portail_salarie') && <SidebarItem icon={ClipboardList} label="Sondages" to="/mes-sondages" active={window.location.pathname === '/mes-sondages'} collapsed={isCollapsed} />}
                            {isModuleActive('portail_salarie') && <SidebarItem icon={ListChecks} label="Mon intégration" to="/mon-integration" active={window.location.pathname === '/mon-integration'} collapsed={isCollapsed} />}
                            {isModuleActive('portail_salarie') && <SidebarItem icon={Receipt} label="Notes de frais" to="/mes-notes-de-frais" active={window.location.pathname === '/mes-notes-de-frais'} collapsed={isCollapsed} />}
                            {isModuleActive('portail_salarie') && user?.time_clock_enabled !== false && <SidebarItem icon={Clock} label="Pointage" to="/pointage" active={window.location.pathname === '/pointage'} collapsed={isCollapsed} />}
                            {isModuleActive('portail_salarie') && <SidebarItem icon={MessageSquare} label="Messages" to="/mes-messages" active={window.location.pathname === '/mes-messages'} collapsed={isCollapsed} badge={myMessagesUnreadCount} />}
                        </>
                    ) : user?.type === 'admin' || user?.role === 'superadmin' ? (
                        <>
                            {!isCollapsed && <div className="mt-3 mb-1 px-3 text-[9px] font-bold text-slate-600 dark:text-content-muted uppercase tracking-widest">Administration</div>}
                            <SidebarItem icon={Users} label="Gestion Clients" to="/admin/clients" active={window.location.pathname.startsWith('/admin/clients')} collapsed={isCollapsed} />
                            <SidebarItem icon={UserPlus} label="Demandes Collaborateurs" to="/admin/collaborator-requests" active={window.location.pathname === '/admin/collaborator-requests'} collapsed={isCollapsed} badge={pendingCollabRequestCount} />
                            {window.location.hostname !== 'gestion.l-iamani.com' && <SidebarItem icon={Zap} label="Demandes d'automatisation" to="/admin/exception-service-automation-requests" active={window.location.pathname === '/admin/exception-service-automation-requests'} collapsed={isCollapsed} />}
                            <SidebarItem icon={ShieldAlert} label="Journal Sentinel" to="/admin/sentinel" active={window.location.pathname === '/admin/sentinel'} collapsed={isCollapsed} />
                            <SidebarItem icon={CalendarCheck} label="RDV Pro" to="/rdv-pro" active={window.location.pathname === '/rdv-pro'} collapsed={isCollapsed} />
                            <SidebarItem icon={Users} label="CRM Contacts" to="/crm" active={window.location.pathname === '/crm'} collapsed={isCollapsed} />
                            <Link
                                to="/admin/support"
                                className={`group flex items-center ${isCollapsed ? 'justify-center' : 'gap-2'} ${isCollapsed ? 'px-0 py-1.5' : 'px-3 py-2'} rounded-lg transition-all relative ${
                                    window.location.pathname === '/admin/support' ? 'bg-brand-500/10 text-brand-700 dark:text-brand-300' : 'text-content-muted hover:bg-surface-soft hover:text-content'
                                }`}
                                title={isCollapsed ? 'Support Client' : undefined}
                            >
                                {window.location.pathname === '/admin/support' && !isCollapsed && (
                                    <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full bg-brand-500" />
                                )}
                                <span className={`inline-flex items-center justify-center ${isCollapsed ? 'w-8 h-8 rounded-lg group-hover:bg-slate-200 dark:bg-surface-soft' : 'w-5 h-5'} transition-all relative`}>
                                    <MessageSquare className="w-[18px] h-[18px] shrink-0" />
                                    {supportUnreadCount > 0 && (
                                        <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-red-500 text-content text-[10px] font-bold flex items-center justify-center">
                                            {supportUnreadCount > 99 ? '99+' : supportUnreadCount}
                                        </span>
                                    )}
                                </span>
                                {!isCollapsed && <span className="font-medium text-[15px] min-w-0 truncate">Support client</span>}
                            </Link>
                        </>
                    ) : (
                        <>
                            {!isCollapsed && <div className="mt-3 mb-1 px-3 text-[9px] font-bold text-slate-600 dark:text-content-muted uppercase tracking-widest">Mes Modules</div>}
                            {user?.client_type === 'prestation' ? (
                                <>
                                    {(isModuleActive('employes') || isModuleActive('portail_salarie')) && (
                                        <SidebarCategory icon={Users} label="RH & équipe" collapsed={isCollapsed} defaultOpen={['/employes', '/pointage-admin'].includes(window.location.pathname)}>
                                            {isModuleActive('employes') && <SidebarItem icon={Users} label="Postes & Employés" to="/employes" active={window.location.pathname === '/employes'} collapsed={isCollapsed} />}
                                            {isModuleActive('portail_salarie') && <SidebarItem icon={Clock} label="Pointage" to="/pointage-admin" active={window.location.pathname === '/pointage-admin'} collapsed={isCollapsed} />}
                                        </SidebarCategory>
                                    )}
                                    {(isModuleActive('demande_conges') || isModuleActive('portail_salarie')) && (
                                        <SidebarCategory icon={MessageSquare} label="Messagerie" collapsed={isCollapsed} defaultOpen={['/messagerie', '/notes-de-frais', '/demandes-conges'].includes(window.location.pathname)} badge={pendingAbsenceCount + teamMessagesUnreadCount + pendingExpenseCount}>
                                            {isModuleActive('portail_salarie') && <SidebarItem icon={MessageSquare} label="Messages" to="/messagerie" active={window.location.pathname === '/messagerie'} collapsed={isCollapsed} badge={teamMessagesUnreadCount} />}
                                            {isModuleActive('portail_salarie') && <SidebarItem icon={Receipt} label="Notes de frais" to="/notes-de-frais" active={window.location.pathname === '/notes-de-frais'} collapsed={isCollapsed} badge={pendingExpenseCount} />}
                                            {isModuleActive('demande_conges') && <SidebarItem icon={Users} label="Demande de congé(s)" to="/demandes-conges" active={window.location.pathname === '/demandes-conges'} collapsed={isCollapsed} badge={pendingAbsenceCount} />}
                                        </SidebarCategory>
                                    )}
                                    {isModuleActive('planning_rdv') && (
                                        <SidebarCategory icon={Calendar} label="Planning & agenda" collapsed={isCollapsed} defaultOpen={['/planning-rdv'].includes(window.location.pathname)} badge={pendingBookingRequestCount}>
                                            <SidebarItem icon={CalendarClock} label="Planning"
                                                to={nextPendingBookingRequest ? `/planning-rdv?focus_date=${nextPendingBookingRequest.date}&focus_id=${nextPendingBookingRequest.id}` : '/planning-rdv'}
                                                active={window.location.pathname === '/planning-rdv'} collapsed={isCollapsed} badge={pendingBookingRequestCount} />
                                        </SidebarCategory>
                                    )}
                                    {(isModuleActive('crm') || isModuleActive('prestations')) && (
                                        <SidebarCategory icon={Building2} label="Relation & opérations client" collapsed={isCollapsed} defaultOpen={['/crm', '/prestations'].includes(window.location.pathname)}>
                                            {isModuleActive('crm') && <SidebarItem icon={Users} label="CRM Contacts" to="/crm" active={window.location.pathname === '/crm'} collapsed={isCollapsed} />}
                                            {isModuleActive('prestations') && <SidebarItem icon={Package} label="Prestations" to="/prestations" active={window.location.pathname === '/prestations'} collapsed={isCollapsed} />}
                                        </SidebarCategory>
                                    )}
                                    {isModuleActive('facture') && (
                                        <SidebarCategory icon={FileText} label="Facturation" collapsed={isCollapsed} defaultOpen={['/factures'].includes(window.location.pathname)}>
                                            <SidebarItem icon={FileText} label="Factures" to="/factures" active={window.location.pathname === '/factures'} collapsed={isCollapsed} />
                                        </SidebarCategory>
                                    )}
                                    {(isModuleActive('todo') || isModuleActive('notes')) && (
                                        <SidebarCategory icon={ListChecks} label="Personnel" collapsed={isCollapsed} defaultOpen={['/todo', '/notes'].includes(window.location.pathname)}>
                                            {isModuleActive('todo') && <SidebarItem icon={ListChecks} label="ToDo" to="/todo" active={window.location.pathname === '/todo'} collapsed={isCollapsed} />}
                                            {isModuleActive('notes') && <SidebarItem icon={NotebookText} label="Notes" to="/notes" active={window.location.pathname === '/notes'} collapsed={isCollapsed} />}
                                        </SidebarCategory>
                                    )}
                                </>
                            ) : (
                                <>
                                    {(isModuleActive('employes') || isModuleActive('portail_salarie')) && (
                                        <SidebarCategory icon={Users} label="RH & équipe" collapsed={isCollapsed} defaultOpen={['/employes', '/pointage-admin'].includes(window.location.pathname)}>
                                            {isModuleActive('employes') && <SidebarItem icon={Users} label="Postes & Employés" to="/employes" active={window.location.pathname === '/employes'} collapsed={isCollapsed} />}
                                            {isModuleActive('portail_salarie') && <SidebarItem icon={Clock} label="Pointage" to="/pointage-admin" active={window.location.pathname === '/pointage-admin'} collapsed={isCollapsed} />}
                                        </SidebarCategory>
                                    )}
                                    {(isModuleActive('demande_conges') || isModuleActive('portail_salarie')) && (
                                        <SidebarCategory icon={MessageSquare} label="Messagerie" collapsed={isCollapsed} defaultOpen={['/messagerie', '/notes-de-frais', '/demandes-conges'].includes(window.location.pathname)} badge={pendingAbsenceCount + teamMessagesUnreadCount + pendingExpenseCount}>
                                            {isModuleActive('portail_salarie') && <SidebarItem icon={MessageSquare} label="Messages" to="/messagerie" active={window.location.pathname === '/messagerie'} collapsed={isCollapsed} badge={teamMessagesUnreadCount} />}
                                            {isModuleActive('portail_salarie') && <SidebarItem icon={Receipt} label="Notes de frais" to="/notes-de-frais" active={window.location.pathname === '/notes-de-frais'} collapsed={isCollapsed} badge={pendingExpenseCount} />}
                                            {isModuleActive('demande_conges') && <SidebarItem icon={Users} label="Demande de congé(s)" to="/demandes-conges" active={window.location.pathname === '/demandes-conges'} collapsed={isCollapsed} badge={pendingAbsenceCount} />}
                                        </SidebarCategory>
                                    )}
                                    {(isModuleActive('planning') || isModuleActive('exception_service')) && (
                                        <SidebarCategory icon={Calendar} label="Planning & agenda" collapsed={isCollapsed} defaultOpen={['/planning', '/exception-service'].includes(window.location.pathname)}>
                                            {isModuleActive('planning') && <SidebarItem icon={Calendar} label="Planning" to="/planning" active={window.location.pathname === '/planning'} collapsed={isCollapsed} />}
                                            {isModuleActive('exception_service') && <SidebarItem icon={UtensilsCrossed} label="Service d'exception" to="/exception-service" active={window.location.pathname === '/exception-service'} collapsed={isCollapsed} />}
                                        </SidebarCategory>
                                    )}
                                    {/* « Commercial » regroupe ce qui se vend à un client : la privatisation, les menus que
                                        ses convives choisissent, et la carte qui les alimente. Ces trois écrans étaient
                                        rangés sous « Planning & agenda », où ils n'avaient rien à voir avec un planning
                                        d'équipe. Demande d'Emeni (2026-08-25). */}
                                    {(isModuleActive('evenementiel') || isModuleActive('suivi_privatisation') || isModuleActive('menus_convives')) && (
                                        <SidebarCategory icon={Briefcase} label="Commercial" collapsed={isCollapsed} defaultOpen={['/evenementiel', '/suivi-privatisation', '/menus-convives'].some((path) => window.location.pathname.startsWith(path))}>
                                            {isModuleActive('evenementiel') && <SidebarItem icon={Briefcase} label="Événementiel" to="/evenementiel" active={window.location.pathname === '/evenementiel'} collapsed={isCollapsed} />}
                                            {isModuleActive('suivi_privatisation') && <SidebarItem icon={Briefcase} label="Suivi privatisation" to="/suivi-privatisation" active={window.location.pathname.startsWith('/suivi-privatisation')} collapsed={isCollapsed} />}
                                            {isModuleActive('menus_convives') && <SidebarItem icon={Utensils} label="Menus & Convives" to="/menus-convives" active={window.location.pathname.startsWith('/menus-convives')} collapsed={isCollapsed} />}
                                        </SidebarCategory>
                                    )}
                                    {/* « Cuisine » sépare ce qui se PRÉPARE de ce qui se VEND. Menu & Plats était rangé
                                        sous Commercial, où il voisinait avec le suivi des affaires alors qu'il s'agit du
                                        catalogue de la cuisine ; le Mode terrain, lui, n'était atteignable que par un lien
                                        depuis un événement, ce qui obligeait un chef à traverser le module commercial un
                                        soir de service. Demande d'Emeni (2026-08-26). */}
                                    {(isModuleActive('mode_terrain') || isModuleActive('menu_plats')) && (
                                        <SidebarCategory icon={ChefHat} label="Cuisine" collapsed={isCollapsed} defaultOpen={['/mode-terrain', '/menu-plats'].some((path) => window.location.pathname.startsWith(path))}>
                                            {isModuleActive('mode_terrain') && <SidebarItem icon={ChefHat} label="Mode terrain" to="/mode-terrain" active={window.location.pathname.startsWith('/mode-terrain')} collapsed={isCollapsed} />}
                                            {isModuleActive('menu_plats') && <SidebarItem icon={Utensils} label="Menu & Plats" to="/menu-plats" active={window.location.pathname.startsWith('/menu-plats')} collapsed={isCollapsed} />}
                                        </SidebarCategory>
                                    )}
                                    {(isModuleActive('crm') || isModuleActive('rdv_pro')) && (
                                        <SidebarCategory icon={Building2} label="Relation & opérations client" collapsed={isCollapsed} defaultOpen={['/crm', '/rdv-pro'].includes(window.location.pathname)}>
                                            {isModuleActive('crm') && <SidebarItem icon={Users} label="CRM Contacts" to="/crm" active={window.location.pathname === '/crm'} collapsed={isCollapsed} />}
                                            {isModuleActive('rdv_pro') && <SidebarItem icon={CalendarCheck} label="RDV Pro" to="/rdv-pro" active={window.location.pathname === '/rdv-pro'} collapsed={isCollapsed} />}
                                        </SidebarCategory>
                                    )}
                                    {(isModuleActive('fournisseurs') || isModuleActive('commandes') || isModuleActive('extras_partenaires') || isModuleActive('extras')) && (
                                        <SidebarCategory icon={Truck} label="Fournisseurs & Partenaires" collapsed={isCollapsed} defaultOpen={['/fournisseurs', '/commandes', '/extras-partenaires', '/extras'].some((p) => window.location.pathname.startsWith(p))}>
                                            {isModuleActive('fournisseurs') && <SidebarItem icon={Truck} label="Fournisseurs" to="/fournisseurs" active={window.location.pathname.startsWith('/fournisseurs')} collapsed={isCollapsed} />}
                                            {isModuleActive('commandes') && <SidebarItem icon={ClipboardList} label="Commandes" to="/commandes" active={window.location.pathname === '/commandes'} collapsed={isCollapsed} />}
                                            {isModuleActive('extras_partenaires') && <SidebarItem icon={HardHat} label="Partenaires Extras" to="/extras-partenaires" active={window.location.pathname.startsWith('/extras-partenaires')} collapsed={isCollapsed} />}
                                            {isModuleActive('extras') && <SidebarItem icon={HardHat} label="Extras" to="/extras" active={window.location.pathname === '/extras'} collapsed={isCollapsed} />}
                                        </SidebarCategory>
                                    )}
                                    {isModuleActive('facture') && (
                                        <SidebarCategory icon={FileText} label="Facturation" collapsed={isCollapsed} defaultOpen={['/factures'].includes(window.location.pathname)}>
                                            <SidebarItem icon={FileText} label="Factures" to="/factures" active={window.location.pathname === '/factures'} collapsed={isCollapsed} />
                                        </SidebarCategory>
                                    )}
                                    {(isModuleActive('todo') || isModuleActive('notes')) && (
                                        <SidebarCategory icon={ListChecks} label="Personnel" collapsed={isCollapsed} defaultOpen={['/todo', '/notes'].includes(window.location.pathname)}>
                                            {isModuleActive('todo') && <SidebarItem icon={ListChecks} label="ToDo" to="/todo" active={window.location.pathname === '/todo'} collapsed={isCollapsed} />}
                                            {isModuleActive('notes') && <SidebarItem icon={NotebookText} label="Notes" to="/notes" active={window.location.pathname === '/notes'} collapsed={isCollapsed} />}
                                        </SidebarCategory>
                                    )}
                                </>
                            )}
                            {user?.type !== 'employe' && (
                                <>
                                    {!isCollapsed && <div className="mt-3 mb-1 px-3 text-[9px] font-bold text-slate-600 dark:text-content-muted uppercase tracking-widest">Support</div>}
                                    <button
                                        onClick={() => setShowSupportModal(true)}
                                        className={`w-full group flex items-center ${isCollapsed ? 'justify-center' : 'gap-2'} ${isCollapsed ? 'px-0 py-1.5' : 'px-3 py-2'} rounded-lg transition-all text-[var(--text-secondary)] hover:bg-[var(--interactive-hover)] hover:text-[var(--text-primary)] relative`}
                                        title={isCollapsed ? 'Support' : undefined}
                                    >
                                        <span className={`inline-flex items-center justify-center ${isCollapsed ? 'w-8 h-8 rounded-lg group-hover:bg-[var(--interactive-hover)]' : 'w-5 h-5'} transition-all relative`}>
                                            <MessageSquare className="w-[18px] h-[18px] shrink-0" />
                                            {clientSupportUnreadCount > 0 && (
                                                <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-red-500 text-content text-[10px] font-bold flex items-center justify-center">
                                                    {clientSupportUnreadCount > 99 ? '99+' : clientSupportUnreadCount}
                                                </span>
                                            )}
                                        </span>
                                        {!isCollapsed && <span className="font-medium text-[15px] min-w-0 truncate">Support client</span>}
                                    </button>
                                </>
                            )}
                        </>
                    )}
                </nav>

                <div className="mt-auto pt-3 border-t border-[var(--border-color)]">
                    <button
                        onClick={toggleTheme}
                        className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'gap-2'} px-3 py-2 text-[var(--text-secondary)] hover:bg-[var(--interactive-hover)] hover:text-[var(--text-primary)] rounded-lg transition-all font-medium mb-2`}
                        title={isCollapsed ? (theme === 'dark' ? 'Passer en clair' : 'Passer en sombre') : undefined}
                    >
                        {theme === 'dark' ? <Sun size={17} /> : <Moon size={17} />}
                        {!isCollapsed && <span className="text-[14px]">{theme === 'dark' ? 'Mode clair' : 'Mode sombre'}</span>}
                    </button>

                    <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-2'} px-2 mb-3`}>
                        <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-[var(--text-primary)] border border-[var(--border-color)] bg-[var(--bg-soft)]">
                            {user?.name ? user.name[0].toUpperCase() : '?'}
                        </div>
                        {!isCollapsed && (
                            <div className="flex-1 min-w-0">
                                <p className="text-[15px] font-bold truncate text-[var(--text-primary)]">{user?.name}</p>
                                <p className="text-[11px] text-[var(--text-muted)] truncate capitalize">{user?.type}</p>
                            </div>
                        )}
                    </div>
                    <button
                        onClick={() => navigate((user?.type === 'admin' || user?.role === 'superadmin') ? '/admin/settings' : '/parametres')}
                        className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'gap-2'} px-3 py-2 text-[var(--text-secondary)] hover:bg-[var(--interactive-hover)] rounded-lg transition-all font-medium mb-1`}
                        title={isCollapsed ? 'Paramètres' : undefined}
                    >
                        <Settings size={17} />
                        {!isCollapsed && <span className="text-[14px]">Paramètres</span>}
                    </button>
                    <button
                        onClick={handleLogout}
                        className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'gap-2'} px-3 py-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-all font-medium`}
                        title={isCollapsed ? 'Déconnexion' : undefined}
                    >
                        <LogOut size={17} />
                        {!isCollapsed && <span className="text-[14px]">Déconnexion</span>}
                    </button>
                </div>
            </motion.aside>

            {/* Main Content */}
            <main className="flex-1 min-w-0 flex flex-col h-screen overflow-hidden transition-colors duration-200">
                {/* Mobile Header */}
                <div className="md:hidden flex items-center justify-between p-4 border-b border-[var(--border-color)] bg-[var(--bg-sidebar)] z-30 flex-shrink-0">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center overflow-hidden">
                            {resolvedLogoUrl ? (
                                <img src={resolvedLogoUrl} alt="Logo" className="w-full h-full object-cover" />
                            ) : (
                                <ShieldCheck className="text-content" size={20} />
                            )}
                        </div>
                        <span className="font-bold text-xl truncate">{user?.company_name || "L'IAmani"}</span>
                    </div>
                    <button onClick={() => setIsMobileMenuOpen(true)} className="p-2 -mr-2 text-[var(--text-primary)]">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16"/></svg>
                    </button>
                </div>
                
                <div className="flex-1 overflow-auto p-4 md:p-10">
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        {children}
                    </motion.div>
                </div>
                {user?.type !== 'admin' && (
                    <SupportModal 
                        open={showSupportModal} 
                        onClose={() => {
                            setShowSupportModal(false);
                            refreshClientUnreadCount();
                        }} 
                        canOpen 
                        onUnreadCountChanged={refreshClientUnreadCount}
                    />
                )}
            </main>
            {isModuleActive('todo') && <FloatingTodoWidget />}
        </div>
    );
}

// --- Views ---

const LoginView = () => {
    const { user, login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    // URL demandée avant d'être redirigé vers /login (ex: lien de booking d'extras avec ?bookEvent=...),
    // posée par Layout — uniquement un chemin interne (jamais fourni par une source externe), donc sûr à
    // réutiliser tel quel. Demande d'Emeni (2026-08-22).
    const fromPath = (location.state as any)?.from;
    const safeFromPath = typeof fromPath === 'string' && fromPath.startsWith('/') && !fromPath.startsWith('//') ? fromPath : null;

    React.useEffect(() => {
        if (user) navigate(safeFromPath || '/');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, navigate]);

    const [identifier, setIdentifier] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [error, setError] = React.useState('');
    const [showLegalModal, setShowLegalModal] = React.useState(false);
    const [showPrivacyModal, setShowPrivacyModal] = React.useState(false);
    const [loginLogo, setLoginLogo] = React.useState<string | null>(null);

    // Widget flottant "Réserver en ligne" du super admin (démarchage de potentiels futurs clients) —
    // injecté dynamiquement uniquement si activé côté Compte, jamais codé en dur, pour disparaître
    // immédiatement dès que le super admin désactive son toggle.
    React.useEffect(() => {
        let scriptEl: HTMLScriptElement | null = null;
        publicRdvProBookingApi.getPlatformWidget().then((data: any) => {
            if (!data?.enabled || !data?.client_id) return;
            scriptEl = document.createElement('script');
            scriptEl.src = '/booking-widget.js';
            scriptEl.setAttribute('data-client-id', data.client_id);
            scriptEl.setAttribute('data-mode', 'rdv-pro');
            if (data.label) scriptEl.setAttribute('data-label', data.label);
            document.body.appendChild(scriptEl);
        }).catch(() => {});
        // Retirer le <script> ne défait pas ce qu'il a déjà fait (le script injecte lui-même son bouton
        // flottant + overlay directement dans document.body, hors du contrôle de React) — sans ce nettoyage
        // manuel, la bulle restait visible en permanence après connexion, sur tout le tableau de bord.
        return () => {
            if (scriptEl) document.body.removeChild(scriptEl);
            document.getElementById('iamani-rdv-widget-btn')?.remove();
            document.getElementById('iamani-rdv-widget-overlay')?.remove();
        };
    }, []);

    React.useEffect(() => {
        adminApi.getGlobalSettings().then(data => {
            if (data?.loginLogo) setLoginLogo(data.loginLogo);
        }).catch(e => console.error(e));

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setShowLegalModal(false);
                setShowPrivacyModal(false);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);
    const stars = React.useMemo(() => Array.from({ length: 160 }, (_, i) => ({
        id: i,
        left: `${(i * 7.17) % 100}%`,
        top: `${(i * 11.43) % 100}%`,
        size: 1.5 + (i % 4),
        duration: 7 + (i % 7),
        delay: (i % 18) * 0.35,
        drift: ((i % 7) - 3) * 10,
        glow: 7 + (i % 5) * 3,
    })), []);
    const meteors = React.useMemo(() => Array.from({ length: 16 }, (_, i) => ({
        id: i,
        left: `${4 + i * 6.2}%`,
        delay: i * 0.9,
        duration: 3.4 + (i % 3) * 0.7,
    })), []);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError('');
        try {
            // Lit les valeurs directement depuis le DOM (via FormData) plutôt que le seul state React :
            // sur iOS Safari, l'auto-remplissage (mots de passe enregistrés/Trousseau) met parfois à jour
            // visuellement le champ sans déclencher l'événement onChange de React, laissant le state
            // désynchronisé de ce qui est réellement affiché/saisi.
            const formData = new FormData(e.currentTarget);
            const submittedIdentifier = String(formData.get('identifier') || identifier).trim();
            const submittedPassword = String(formData.get('password') || password);
            const data = await authApi.login({ identifier: submittedIdentifier, password: submittedPassword });
            login(data.token, data.user);
            if (data.user?.type === 'employe') {
                navigate(safeFromPath || (data.user?.has_pending_onboarding ? '/mon-integration' : '/planning'));
            } else {
                navigate(safeFromPath || '/');
            }
        } catch (err: any) {
            setError(err.message);
        }
    };

    return (
        <div className="min-h-screen relative overflow-hidden bg-[#030712] flex flex-col items-center justify-center p-6 gap-8">
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(56,189,248,0.16),transparent_0%,transparent_42%),radial-gradient(circle_at_bottom,_rgba(99,102,241,0.16),transparent_0%,transparent_48%)]" />
                {stars.map((star) => (
                    <motion.span
                        key={star.id}
                        className="absolute rounded-full bg-white/90"
                        style={{ left: star.left, top: star.top, width: star.size, height: star.size, boxShadow: `0 0 ${star.glow}px rgba(255,255,255,0.8)` }}
                        animate={{ y: [-30, 820], x: [0, star.drift], opacity: [0, 0.95, 0.65, 0] }}
                        transition={{ duration: star.duration, repeat: Infinity, ease: 'linear', delay: star.delay }}
                    />
                ))}
                {meteors.map((meteor) => (
                    <motion.div
                        key={`meteor-${meteor.id}`}
                        className="absolute h-px rounded-full bg-gradient-to-r from-white via-cyan-200 to-transparent"
                        style={{ left: meteor.left, top: '-10%', width: 140 }}
                        animate={{ x: [0, -180], y: [0, 520], opacity: [0, 1, 0] }}
                        transition={{ duration: meteor.duration, repeat: Infinity, ease: 'easeIn', delay: meteor.delay }}
                    />
                ))}
            </div>

            <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="relative z-10 w-full max-w-md bg-[#0f172a]/90 backdrop-blur-md rounded-2xl shadow-2xl p-10 border border-slate-800"
            >
                <div className="flex flex-col items-center mb-10">
                    <Link to="/" className={`rounded-xl flex items-center justify-center mb-4 transition-opacity hover:opacity-80 ${loginLogo ? 'h-24 max-w-[280px]' : 'w-24 h-24 overflow-hidden bg-white shadow-lg shadow-cyan-400/20'}`}>
                        {loginLogo ? (
                            <img src={loginLogo} alt="Logo IAmani" className="h-full w-auto max-w-full object-contain" />
                        ) : (
                            <ShieldCheck className="text-slate-800" size={40} />
                        )}
                    </Link>
                    <h1 className="text-2xl font-bold tracking-tight text-white">Connexion</h1>
                    <p className="text-gray-400 text-sm mt-2 text-center">Entrez vos identifiants pour accéder à la plateforme L'IAmani</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {error && (
                        <div className="p-4 bg-red-500/10 text-red-400 text-sm rounded-lg flex items-center gap-2 border border-red-500/20">
                            <XCircle size={16} />
                            {error}
                        </div>
                    )}
                    <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Identifiant</label>
                        <input
                            type="text"
                            name="identifier"
                            value={identifier}
                            onChange={(e) => setIdentifier(e.target.value)}
                            className="w-full px-4 py-3 rounded-lg bg-[#0f172a] border border-slate-800 text-white focus:border-slate-700 focus:ring-0 transition-all outline-none"
                            placeholder="Email ou Pseudo"
                            required
                            autoComplete="username"
                            autoCapitalize="none"
                            autoCorrect="off"
                            spellCheck={false}
                        />
                    </div>
                    <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Mot de passe</label>
                        <input
                            type="password"
                            name="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full px-4 py-3 rounded-lg bg-[#0f172a] border border-slate-800 text-white focus:border-slate-700 focus:ring-0 transition-all outline-none"
                            placeholder="••••••••"
                            required
                            autoComplete="current-password"
                        />
                    </div>
                    <button 
                        type="submit"
                        className="w-full bg-white text-slate-900 py-4 rounded-lg font-bold hover:bg-gray-200 transition-all shadow-lg shadow-white/5"
                    >
                        Se connecter
                    </button>
                </form>

                <div className="mt-8 pt-8 border-t border-slate-800 text-center space-y-4">
                    <p className="text-xs text-gray-400">
                        Plateforme sécurisée réservée aux clients et collaborateurs autorisés.
                    </p>
                </div>
            </motion.div>

            {/* Liens légaux sous la carte */}
            <div className="relative z-20 flex justify-center gap-6 text-[11px] text-gray-400 uppercase tracking-widest font-semibold">
                <button type="button" onClick={() => setShowLegalModal(true)} className="hover:text-white transition-colors">Mentions Légales</button>
                <span>&bull;</span>
                <button type="button" onClick={() => setShowPrivacyModal(true)} className="hover:text-white transition-colors">Politique de Confidentialité</button>
            </div>

            {/* Modal Mentions Légales */}
            {showLegalModal && (
                <div 
                    className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="legal-modal-title"
                >
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-[#0f172a] border border-slate-800 rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col"
                    >
                        <div className="flex justify-between items-center p-6 border-b border-slate-800">
                            <h2 id="legal-modal-title" className="text-xl font-bold text-white">Mentions Légales</h2>
                            <button onClick={() => setShowLegalModal(false)} className="text-gray-400 hover:text-white" aria-label="Fermer">
                                <XCircle size={24} />
                            </button>
                        </div>
                        <div className="p-6 overflow-y-auto custom-scrollbar flex-1 text-sm text-gray-400">
                            <LegalMentions />
                        </div>
                    </motion.div>
                </div>
            )}

            {/* Modal Politique de Confidentialité */}
            {showPrivacyModal && (
                <div 
                    className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="privacy-modal-title"
                >
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-[#0f172a] border border-slate-800 rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col"
                    >
                        <div className="flex justify-between items-center p-6 border-b border-slate-800">
                            <h2 id="privacy-modal-title" className="text-xl font-bold text-white">Politique de Confidentialité</h2>
                            <button onClick={() => setShowPrivacyModal(false)} className="text-gray-400 hover:text-white" aria-label="Fermer">
                                <XCircle size={24} />
                            </button>
                        </div>
                        <div className="p-6 overflow-y-auto custom-scrollbar flex-1 text-sm text-gray-400">
                            <PrivacyPolicy />
                        </div>
                    </motion.div>
                </div>
            )}
        </div>
    );
};

const DigitalClock = () => {
    const [time, setTime] = React.useState(new Date());

    React.useEffect(() => {
        const timer = setInterval(() => setTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    const hours = String(time.getHours()).padStart(2, '0');
    const minutes = String(time.getMinutes()).padStart(2, '0');
    const seconds = String(time.getSeconds()).padStart(2, '0');

    return (
        <div className="flex flex-col items-center justify-center h-full">
            <div className="text-[var(--text-muted)] text-[9px] font-bold uppercase tracking-widest mb-1">Heure actuelle</div>
            <div className="flex items-center gap-2 text-3xl md:text-4xl font-black tracking-tighter text-[var(--text-primary)] font-mono">
                <span>{hours}</span>
                <span className="animate-pulse text-brand-500">:</span>
                <span>{minutes}</span>
                <span className="animate-pulse text-brand-500 text-xl md:text-2xl">:</span>
                <span className="text-xl md:text-2xl text-[var(--text-muted)] w-[1.2ch]">{seconds}</span>
            </div>
            <div className="text-[var(--text-muted)] text-[10px] mt-1 font-medium capitalize">
                {time.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
            </div>
        </div>
    );
};

const CollaboratorRequestModal = ({ onClose }: { onClose: () => void }) => {
    const [form, setForm] = React.useState<{ full_name: string; username: string; email: string; role: string; modules_access: string[] }>({
        full_name: '', username: '', email: '', role: '', modules_access: []
    });
    const [activeModules, setActiveModules] = React.useState<string[]>([]);
    const [submitting, setSubmitting] = React.useState(false);
    const [error, setError] = React.useState('');
    const [done, setDone] = React.useState(false);

    React.useEffect(() => {
        (async () => {
            try {
                const modules = await authApi.getMyModules();
                const names = (Array.isArray(modules) ? modules : [])
                    .filter((m: any) => m.is_active === 1)
                    .map((m: any) => m.module_name);
                setActiveModules(names);
                setForm((f) => ({ ...f, modules_access: names }));
            } catch (e) {
                console.error('Failed to load active modules', e);
            }
        })();
    }, []);

    const toggleModule = (name: string) => {
        setForm((f) => ({
            ...f,
            modules_access: f.modules_access.includes(name)
                ? f.modules_access.filter((m) => m !== name)
                : [...f.modules_access, name]
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSubmitting(true);
        try {
            await dashboardApi.createCollaboratorRequest(form);
            setDone(true);
        } catch (err: any) {
            setError(err?.message || "Erreur lors de l'envoi de la demande.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 md:p-6 overflow-y-auto transition-colors duration-200">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="w-full max-w-md bg-white dark:bg-[var(--bg-app)] rounded-2xl shadow-2xl border border-border my-auto flex flex-col max-h-[90vh] transition-colors duration-200"
            >
                <div className="overflow-y-auto flex-1">
                    <div className="p-8">
                        {done ? (
                            <div className="text-center py-6 space-y-3">
                                <CheckCircle2 size={40} className="mx-auto text-emerald-500" />
                                <h2 className="text-xl font-bold text-slate-900 dark:text-content">Demande envoyée</h2>
                                <p className="text-sm text-content-muted">
                                    Votre demande a été transmise à l'administrateur. Vous recevrez une réponse par email.
                                </p>
                                <button
                                    onClick={onClose}
                                    className="mt-2 px-6 py-3 rounded-lg font-bold bg-brand-600 text-brand-fg hover:bg-brand-700 transition-all"
                                >
                                    Fermer
                                </button>
                            </div>
                        ) : (
                            <>
                                <h2 className="text-2xl font-bold mb-4 text-slate-900 dark:text-content flex items-center gap-2">
                                    <UserPlus size={22} className="text-brand-600 dark:text-brand-400" /> Demande de compte collaborateur
                                </h2>

                                <div className="flex gap-2.5 p-3.5 rounded-xl bg-brand-500/10 border border-brand-500/20 mb-6">
                                    <Info size={16} className="text-brand-600 dark:text-brand-400 shrink-0 mt-0.5" />
                                    <p className="text-[11px] leading-relaxed text-content-muted">
                                        Un compte collaborateur permet à un membre de votre équipe (manager, hôtesse...) de se connecter à la plateforme avec un accès limité à certains modules seulement. <strong className="text-content">Chaque compte est strictement personnel et destiné à une seule personne</strong> — ne partagez jamais ces identifiants entre plusieurs employés, créez une demande distincte pour chacun. Une fois soumise, votre demande est transmise à l'administrateur qui l'acceptera ou la refusera.
                                    </p>
                                </div>

                                {error && <p className="text-xs text-red-400 mb-4">{error}</p>}

                                <form id="collab-request-form" onSubmit={handleSubmit} className="space-y-4">
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Nom complet</label>
                                        <input
                                            type="text"
                                            value={form.full_name}
                                            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                                            className="w-full px-4 py-3 rounded-lg bg-surface border border-gray-300 dark:border-border text-slate-900 dark:text-content focus:border-slate-400 dark:focus:border-border outline-none transition-colors duration-200"
                                            placeholder="Nom de la personne concernée"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-content-muted uppercase tracking-widest">
                                            Pseudo de connexion <span className="text-red-400">*</span>
                                        </label>
                                        <input
                                            type="text" required
                                            value={form.username}
                                            onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase().replace(/\s+/g, '.') })}
                                            className="w-full px-4 py-3 rounded-lg bg-surface border border-gray-300 dark:border-border text-slate-900 dark:text-content focus:border-slate-400 dark:focus:border-border outline-none transition-colors duration-200 font-mono"
                                            placeholder="ex: liamani.direction"
                                            autoComplete="off"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-content-muted uppercase tracking-widest">
                                            Email <span className="text-red-400">*</span>
                                        </label>
                                        <input
                                            type="email" required
                                            value={form.email}
                                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                                            className="w-full px-4 py-3 rounded-lg bg-surface border border-gray-300 dark:border-border text-slate-900 dark:text-content focus:border-slate-400 dark:focus:border-border outline-none transition-colors duration-200"
                                            placeholder="nom@exemple.com"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Rôle / Poste</label>
                                        <input
                                            type="text"
                                            value={form.role}
                                            onChange={(e) => setForm({ ...form, role: e.target.value })}
                                            className="w-full px-4 py-3 rounded-lg bg-surface border border-gray-300 dark:border-border text-slate-900 dark:text-content focus:border-slate-400 dark:focus:border-border outline-none transition-colors duration-200"
                                            placeholder="ex: Manager, Hôtesse..."
                                        />
                                    </div>
                                    <div className="space-y-2 pt-1">
                                        <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Accès aux modules</label>
                                        {activeModules.length === 0 ? (
                                            <p className="text-[11px] text-content-muted">Aucun module actif sur votre compte.</p>
                                        ) : (
                                            <div className="grid grid-cols-2 gap-2">
                                                {ALL_MODULES.filter((m) => activeModules.includes(m.name)).map((m) => {
                                                    const checked = form.modules_access.includes(m.name);
                                                    return (
                                                        <label key={m.name} className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer transition-all text-sm ${checked ? 'border-brand-500/40 bg-brand-500/10 text-content' : 'border-gray-300 dark:border-border text-content-muted hover:text-content'}`}>
                                                            <input
                                                                type="checkbox"
                                                                checked={checked}
                                                                onChange={() => toggleModule(m.name)}
                                                                className="accent-brand-600"
                                                            />
                                                            <span className="font-medium">{m.label}</span>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </form>
                            </>
                        )}
                    </div>
                </div>

                {!done && (
                    <div className="sticky bottom-0 border-t border-border bg-white dark:bg-[var(--bg-app)] p-6 flex gap-4 transition-colors duration-200">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-6 py-3 rounded-lg font-bold border border-gray-300 dark:border-border text-slate-900 dark:text-content hover:bg-slate-100 dark:hover:bg-surface-soft transition-colors duration-200"
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            form="collab-request-form"
                            disabled={submitting}
                            className="flex-1 bg-brand-600 text-brand-fg px-6 py-3 rounded-lg font-bold hover:bg-brand-700 transition-all disabled:opacity-50"
                        >
                            {submitting ? 'Envoi...' : 'Envoyer la demande'}
                        </button>
                    </div>
                )}
            </motion.div>
        </div>
    );
};

const DashboardView = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = React.useState<any[]>([]);
    const [recentActivity, setRecentActivity] = React.useState<any[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [showCollabRequestModal, setShowCollabRequestModal] = React.useState(false);
    const [showSendDocModal, setShowSendDocModal] = React.useState(false);
    // "Demandes récurrentes" (historique des Démarches client envoyées) : déplacé des Réglages vers une
    // tuile dédiée sur le Dashboard, à la demande du propriétaire — ce n'était pas à sa place dans les
    // Réglages généraux. Visible dès que le module CRM est actif (mêmes conditions qu'avant) — sauf pour
    // Prestation, qui y accède désormais directement depuis le Planning ("Paiement RDV"), sur demande du
    // propriétaire de ne plus la dupliquer sur le Dashboard pour ce type de client.
    const [canSeeDemandes, setCanSeeDemandes] = React.useState(false);

    React.useEffect(() => {
        if (user) {
            loadStats();
        }
        if (user?.type === 'client') {
            authApi.getMyModules().then((modules: any) => {
                const names = (Array.isArray(modules) ? modules : []).filter((m: any) => m.is_active === 1).map((m: any) => m.module_name);
                setCanSeeDemandes(names.includes('crm') && (user as any)?.client_type !== 'prestation');
            }).catch(() => setCanSeeDemandes(false));
        }
    }, [user]);

    const loadStats = async () => {
        try {
            if (user?.type === 'admin') {
                const data = await adminApi.getStats();
                setStats([
                    { label: 'Clients Actifs', value: data.clientsCount.toString(), icon: Users, color: 'text-brand-600 dark:text-brand-400', bg: 'bg-brand-500/10' },
                    { label: 'Revenus Mensuels', value: data.revenue, icon: FileText, color: 'text-green-400', bg: 'bg-green-500/10' },
                    { label: 'Modules Activés', value: data.activeModulesCount.toString(), icon: Settings, color: 'text-purple-400', bg: 'bg-purple-500/10' },
                    { label: 'Collaborateurs', value: data.collaboratorsCount.toString(), icon: ShieldCheck, color: 'text-orange-400', bg: 'bg-orange-500/10' },
                ]);
                setRecentActivity([]);
            } else {
                const data = await dashboardApi.getClientStats();
                if (user?.client_type === 'prestation') {
                    setStats([
                        { label: 'Employés', value: String(data?.employes ?? 0), icon: Users, color: 'text-brand-600 dark:text-brand-400', bg: 'bg-brand-500/10' },
                        { label: 'Rendez-vous', value: String(data?.appointments ?? 0), icon: CalendarClock, color: 'text-purple-400', bg: 'bg-purple-500/10' },
                        { label: 'Factures', value: String(data?.factures ?? 0), icon: FileText, color: 'text-orange-400', bg: 'bg-green-500/10' },
                        { label: 'Prestations', value: String(data?.prestationsCatalogCount ?? 0), icon: Package, color: 'text-green-400', bg: 'bg-green-500/10' },
                    ]);
                } else {
                    setStats([
                        { label: 'Employés', value: String(data?.employes ?? 0), icon: Users, color: 'text-brand-600 dark:text-brand-400', bg: 'bg-brand-500/10' },
                        { label: 'Événements', value: String(data?.evenements ?? 0), icon: Calendar, color: 'text-purple-400', bg: 'bg-purple-500/10' },
                        { label: 'Factures', value: String(data?.factures ?? 0), icon: FileText, color: 'text-orange-400', bg: 'bg-green-500/10' },
                        { label: 'Planning', value: String(data?.planning ?? 0), icon: Briefcase, color: 'text-green-400', bg: 'bg-green-500/10' },
                    ]);
                }
                setRecentActivity(Array.isArray(data?.recentActivity) ? data.recentActivity : []);
            }
        } catch (e) {
            console.error(e);
            setRecentActivity([]);
        } finally {
            setLoading(false);
        }
    };

    const formatRelativeTime = (value?: string) => {
        if (!value) return '';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return '';
        const diffMs = Date.now() - date.getTime();
        const diffMin = Math.max(1, Math.floor(diffMs / 60000));
        if (diffMin < 60) return `Il y a ${diffMin} min`;
        const diffHours = Math.floor(diffMin / 60);
        if (diffHours < 24) return `Il y a ${diffHours} h`;
        const diffDays = Math.floor(diffHours / 24);
        if (diffDays < 30) return `Il y a ${diffDays} j`;
        return date.toLocaleDateString();
    };

    const formatActivityDetail = (value?: string) => {
        if (!value) return '';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return value;
        return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    };

    const getActivityPresentation = (type?: string) => {
        switch (type) {
            case 'facture':
                return { icon: FileText, color: 'text-emerald-500' };
            case 'evenement':
                return { icon: Calendar, color: 'text-brand-500' };
            case 'employe':
                return { icon: Users, color: 'text-sky-500' };
            case 'planning':
                return { icon: Briefcase, color: 'text-warning-500' };
            default:
                return { icon: ChevronRight, color: 'text-[var(--text-muted)]' };
        }
    };

    return (
    <div className="space-y-10">
            <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight text-[var(--text-primary)]">Tableau de bord</h1>
                    <p className="text-[var(--text-muted)] mt-1">
                        {user?.type === 'admin'
                            ? 'Vue d\'ensemble de la plateforme L\'IAmani.'
                            : `Bienvenue sur votre espace de gestion, ${user?.name}.`}
                    </p>
                </div>
                {user?.type === 'client' && (
                    <button
                        onClick={() => setShowCollabRequestModal(true)}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-500/10 hover:bg-brand-500/20 border border-brand-500/20 text-brand-600 dark:text-brand-400 text-sm font-bold transition-all self-start md:self-auto"
                    >
                        <UserPlus size={16} /> Faire une demande de compte collaborateur
                    </button>
                )}
            </header>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {stats.map((stat, i) => (
                    <motion.div 
                        key={i} 
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.1 }}
                        className="bg-[var(--bg-card)] p-4 md:p-8 rounded-2xl border border-[var(--border-color)] shadow-sm transition-colors duration-200"
                    >
                        <div className={`w-12 h-12 ${stat.bg} rounded-xl flex items-center justify-center mb-6`}>
                            <stat.icon className={stat.color} size={24} />
                        </div>
                        <p className="text-[var(--text-muted)] text-xs font-bold uppercase tracking-widest">{stat.label}</p>
                        <p className="text-3xl font-bold mt-2 text-[var(--text-primary)]">{stat.value}</p>
                    </motion.div>
                ))}
            </div>

            {canSeeDemandes && user?.client_type === 'restauration' && (
                <button onClick={() => setShowSendDocModal(true)}
                    className="w-full flex items-center justify-between gap-4 bg-[var(--bg-card)] p-4 md:p-6 rounded-2xl border border-[var(--border-color)] shadow-sm hover:border-brand-500/40 transition-colors duration-200 text-left">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-brand-500/10 rounded-xl flex items-center justify-center shrink-0">
                            <Send className="text-brand-600 dark:text-brand-400" size={20} />
                        </div>
                        <div>
                            <p className="font-bold text-[var(--text-primary)]">Envoyer une démarche</p>
                            <p className="text-[var(--text-muted)] text-xs mt-0.5">Par email, à un contact déjà en CRM ou non — un contact sera créé automatiquement s'il n'existe pas encore</p>
                        </div>
                    </div>
                </button>
            )}

            {canSeeDemandes && (
                <button onClick={() => navigate('/demandes-recurrentes')}
                    className="w-full flex items-center justify-between gap-4 bg-[var(--bg-card)] p-4 md:p-6 rounded-2xl border border-[var(--border-color)] shadow-sm hover:border-brand-500/40 transition-colors duration-200 text-left">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-brand-500/10 rounded-xl flex items-center justify-center shrink-0">
                            <ClipboardList className="text-brand-600 dark:text-brand-400" size={22} />
                        </div>
                        <div>
                            <p className="font-bold text-[var(--text-primary)]">Demandes récurrentes</p>
                            <p className="text-[var(--text-muted)] text-xs mt-0.5">Historique des Démarches client envoyées à vos contacts</p>
                        </div>
                    </div>
                    <ChevronRight className="text-[var(--text-muted)] shrink-0" size={20} />
                </button>
            )}

            {showSendDocModal && (
                <SendDocumentByEmailModal onClose={() => setShowSendDocModal(false)} onSent={() => setShowSendDocModal(false)} />
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                <div className="lg:col-span-2 bg-[var(--bg-card)] rounded-2xl border border-[var(--border-color)] p-4 md:p-8 transition-colors duration-200 flex flex-col items-center justify-center min-h-[150px]">
                    <DigitalClock />
                </div>

                <div className="bg-[var(--bg-card)] text-[var(--text-primary)] rounded-2xl p-5 md:p-10 border border-[var(--border-color)] shadow-2xl relative overflow-hidden flex flex-col justify-between transition-colors duration-200">
                    <div className="relative z-10">
                        <h2 className="text-xl font-bold mb-4">
                            {user?.type === 'admin' ? 'Maintenance Système' : "Support L'IAmani"}
                        </h2>
                        <p className="text-[var(--text-muted)] text-sm leading-relaxed mb-8">
                            {user?.type === 'admin' 
                                ? 'Vérifiez l\'état des serveurs et les logs de sécurité de la plateforme.' 
                                : 'Une question sur le fonctionnement, une demande de modification ou mise à jour, une demande d\'ajout d\'un système de gestion, ou tout ce qui pourrait vous faciliter votre travail ? Contactez le support client.'}
                        </p>
                        {user?.type === 'admin' || user?.role === 'superadmin' ? (
                            <button className="bg-[var(--text-primary)] text-[var(--bg-card)] px-8 py-4 rounded-xl font-bold text-sm hover:opacity-90 transition-all shadow-lg">
                                Voir les logs
                            </button>
                        ) : (
                            <div className="text-[var(--text-muted)] text-sm text-center py-4">
                                Le support est disponible dans la barre latérale.
                            </div>
                        )}
                    </div>
                    <div className="absolute -right-10 -bottom-10 w-40 h-40 bg-[var(--interactive-hover)] rounded-full blur-3xl"></div>
                </div>
            </div>

            {showCollabRequestModal && (
                <CollaboratorRequestModal onClose={() => setShowCollabRequestModal(false)} />
            )}

            {user?.type !== 'admin' && (
                <div className="pt-6">
                    <PlanningTableView />
                </div>
            )}
        </div>
    );
};

const SupportAdminView = () => {
    const [status, setStatus] = React.useState<'OPEN' | 'CLOSED'>('OPEN');
    const [tickets, setTickets] = React.useState<any[]>([]);
    const [selected, setSelected] = React.useState<any | null>(null);
    const [messages, setMessages] = React.useState<any[]>([]);
    const [text, setText] = React.useState('');
    const [file, setFile] = React.useState<File | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [sending, setSending] = React.useState(false);

    const loadTickets = React.useCallback(async () => {
        setLoading(true);
        try {
            const data = await supportApi.getAdminTickets(status);
            setTickets(data || []);
            if (data?.length && !selected) {
                setSelected(data[0]);
            }
            if (!data?.length) {
                setSelected(null);
                setMessages([]);
            }
        } finally {
            setLoading(false);
        }
    }, [status, selected]);

    const loadMessages = React.useCallback(async (ticketId: string) => {
        const data = await supportApi.getAdminTicketMessages(ticketId);
        setMessages(Array.isArray(data) ? data : (data?.messages || []));
    }, []);

    React.useEffect(() => { loadTickets(); }, [loadTickets]);
    React.useEffect(() => {
        if (selected?.id) loadMessages(selected.id);
    }, [selected?.id, loadMessages]);

    const send = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selected?.id || (!text.trim() && !file)) return;
        try {
            setSending(true);
            let fileUrl: string | null = null;
            let fileName: string | null = null;
            if (file) {
                const fd = new FormData();
                fd.append('file', file);
                const up = await supportApi.uploadFile(fd);
                fileUrl = up.file_url;
                fileName = up.file_name || file.name;
            }
            await supportApi.sendAdminMessage(selected.id, { message: text.trim() || null, file_url: fileUrl, file_name: fileName });
            setText('');
            setFile(null);
            await loadMessages(selected.id);
            await loadTickets();
        } finally {
            setSending(false);
        }
    };

    const closeTicket = async () => {
        if (!selected?.id) return;
        await supportApi.closeTicket(selected.id);
        setSelected(null);
        setMessages([]);
        await loadTickets();
    };

    const deleteTicket = async () => {
        if (!selected?.id) return;
        if (!confirm('Êtes-vous sûr de vouloir supprimer ce ticket ?')) return;
        try {
            await supportApi.deleteTicket(selected.id);
            setSelected(null);
            setMessages([]);
            await loadTickets();
        } catch (e) {
            alert('Erreur lors de la suppression');
        }
    };

    const isImage = (url?: string) => {
        const u = (url || '').toLowerCase();
        return u.endsWith('.png') || u.endsWith('.jpg') || u.endsWith('.jpeg') || u.endsWith('.gif') || u.endsWith('.webp') || u.endsWith('.svg');
    };
    const resolveUrl = (url: string) => (url.startsWith('http') ? url : `${window.location.origin}${url}`);

    return (
        <div className="space-y-6">
            <header className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight text-content">Support Client</h1>
                    <p className="text-content-muted mt-1">Tickets ouverts et archivés.</p>
                </div>
                <div className="flex gap-2 bg-surface p-1 rounded-xl border border-border">
                    <button onClick={() => setStatus('OPEN')} className={`px-4 py-2 rounded-lg text-sm font-semibold ${status === 'OPEN' ? 'bg-white text-slate-900' : 'text-content-muted'}`}>Ouverts</button>
                    <button onClick={() => setStatus('CLOSED')} className={`px-4 py-2 rounded-lg text-sm font-semibold ${status === 'CLOSED' ? 'bg-white text-slate-900' : 'text-content-muted'}`}>Clôturés</button>
                </div>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="bg-surface border border-border rounded-2xl p-4 lg:col-span-1">
                    {loading ? <p className="text-content-muted">Chargement...</p> : (
                        <div className="space-y-2 max-h-[70vh] overflow-auto">
                            {tickets.length === 0 && <p className="text-content-muted text-sm p-3">Aucun ticket.</p>}
                            {tickets.map((t) => (
                                <button
                                    key={t.id}
                                    onClick={() => setSelected(t)}
                                    className={`w-full text-left p-4 rounded-xl border transition-all ${selected?.id === t.id ? 'border-white/40 bg-surface-soft ring-1 ring-white/10' : 'border-border hover:border-white/20'}`}
                                >
                                    <div className="flex justify-between items-start mb-1">
                                        <p className="text-content font-bold truncate flex-1">{t.company_name}</p>
                                        {t.unread_count > 0 && (
                                            <span className="bg-red-500 text-content text-[10px] px-1.5 py-0.5 rounded-full ml-2 animate-pulse">{t.unread_count}</span>
                                        )}
                                    </div>
                                    <p className="text-[10px] text-content-muted uppercase font-bold tracking-tight mb-1">{t.creator_name || 'Client'}</p>
                                    <p className="text-[10px] text-gray-600">{new Date(t.created_at).toLocaleDateString()}</p>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="bg-[var(--bg-app)] border border-border rounded-2xl lg:col-span-2 flex flex-col h-[70vh] overflow-hidden">
                    {!selected ? (
                        <div className="flex-1 flex items-center justify-center text-content-muted">Sélectionnez un ticket</div>
                    ) : (
                        <>
                            <div className="px-4 py-3 border-b border-border bg-surface flex items-center justify-between">
                                <div>
                                    <p className="text-content font-bold">{selected.company_name || selected.client_name}</p>
                                    <p className="text-xs text-content-muted">Ticket: {selected.id}</p>
                                </div>
                                <div className="flex gap-2">
                                    {selected.status === 'OPEN' && (
                                        <button onClick={closeTicket} className="px-3 py-2 text-sm rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20">Clôturer l'incident</button>
                                    )}
                                    {selected.status === 'CLOSED' && (
                                        <button onClick={deleteTicket} className="px-3 py-2 text-sm rounded-lg bg-red-600/10 text-red-500 hover:bg-red-600/20">Supprimer</button>
                                    )}
                                </div>
                            </div>

                            <div className="flex-1 overflow-y-auto p-4 space-y-3">
                                {messages.map((m) => (
                                    <div key={m.id} className={`flex ${m.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}>
                                        <div className={`max-w-[78%] rounded-2xl px-4 py-3 border ${m.sender_type === 'admin' ? 'bg-surface border-[#C8AA6E]/50 text-content' : 'bg-surface-soft border-border text-content'}`}>
                                            {m.message && <p className="text-sm whitespace-pre-wrap">{m.message}</p>}
                                            {m.file_url && (
                                                <a href={resolveUrl(m.file_url)} target="_blank" rel="noreferrer" className="block mt-2 rounded-lg border border-border p-2 hover:bg-surface-soft">
                                                    {isImage(m.file_url) ? (
                                                        <img src={resolveUrl(m.file_url)} className="max-h-40 rounded-md object-contain" />
                                                    ) : (
                                                        <p className="text-sm text-content-muted">{m.file_name || 'Pièce jointe'}</p>
                                                    )}
                                                </a>
                                            )}
                                            <p className="mt-1 text-[10px] text-content-muted">{new Date(m.created_at).toLocaleString()}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {selected.status === 'OPEN' && (
                                <form onSubmit={send} className="p-4 border-t border-border bg-surface">
                                    <div className="flex items-end gap-2">
                                        <label className="p-2 rounded-lg border border-border text-content-muted hover:bg-surface-soft cursor-pointer">
                                            <input type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                                            <Paperclip size={16} />
                                        </label>
                                        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} className="flex-1 resize-none px-3 py-2 rounded-lg bg-surface border border-border text-content outline-none focus:border-border" placeholder="Répondre au client..." />
                                        <button disabled={sending || (!text.trim() && !file)} className="px-4 py-2 rounded-lg bg-white text-slate-900 font-bold disabled:opacity-50">Envoyer</button>
                                    </div>
                                    {file && <p className="text-xs text-content-muted mt-2">Pièce jointe: {file.name}</p>}
                                </form>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

const AdminClientsView = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [clients, setClients] = React.useState<any[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [showAddModal, setShowAddModal] = React.useState(false);
    const [showSendRegistration, setShowSendRegistration] = React.useState(false);
    const [successData, setSuccessData] = React.useState<any>(null);
    const [selectedFactureClient, setSelectedFactureClient] = React.useState<any | null>(null);
    const [clientFactures, setClientFactures] = React.useState<any[]>([]);
    const [facturesLoading, setFacturesLoading] = React.useState(false);
    const [deletingFactureId, setDeletingFactureId] = React.useState<string | null>(null);
    const [selectedCpClient, setSelectedCpClient] = React.useState<any | null>(null);
    const [clientAbsenceRequests, setClientAbsenceRequests] = React.useState<any[]>([]);
    const [cpLoading, setCpLoading] = React.useState(false);
    const [deletingCpId, setDeletingCpId] = React.useState<string | null>(null);
    const [newClientLogoFile, setNewClientLogoFile] = React.useState<File | null>(null);
    const [newTvaInput, setNewTvaInput] = React.useState('');
    const [clientSearch, setClientSearch] = React.useState('');
    const [newClient, setNewClient] = React.useState({
        name: '',
        email: '',
        username: '',
        company_name: '',
        logo_url: '',
        tva_rates: [20] as number[],
        enable_cover_count: false,
        client_type: 'restauration' as ClientType,
        prestation_location_mode: 'domicile' as 'domicile' | 'entreprise' | 'les_deux',
        modules: getModulesForClientType('restauration').map(m => ({ name: m.name, active: m.name === 'planning' }))
    });

    const handleClientTypeChange = (type: ClientType) => {
        setNewClient(prev => ({
            ...prev,
            client_type: type,
            modules: getModulesForClientType(type).map(m => ({ name: m.name, active: type === 'restauration' && m.name === 'planning' }))
        }));
    };

    React.useEffect(() => {
        if (user) {
            loadClients();
        }
    }, [user]);

    const toCurrency = (value: number) =>
        new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number.isFinite(value) ? value : 0);

    const loadClients = async () => {
        try {
            const data = await adminApi.getClients();
            setClients(data);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const handleAddClient = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            let uploadedLogoUrl = (newClient.logo_url || '').trim() || null;
            if (newClientLogoFile) {
                // Redimensionné/recompressé côté navigateur avant envoi — voir resizeImageForUpload.ts.
                const { base64: logoBase64, mimeType: logoMimeType } = await resizeImageForUpload(newClientLogoFile);
                const upload = await adminApi.uploadLogo(logoBase64, logoMimeType);
                if (!upload.logo_url) {
                    throw new Error(upload.error || 'Erreur upload logo');
                }
                uploadedLogoUrl = upload.logo_url;
            }

            const payload = {
                ...newClient,
                logo_url: uploadedLogoUrl,
                modules: newClient.modules.filter(m => m.active).map(m => m.name)
            };
            const result = await adminApi.createClient(payload);
            setSuccessData({ ...newClient, tempPassword: result.tempPassword, emailSent: result.emailSent, emailError: result.emailError });
            setShowAddModal(false);
            setNewClientLogoFile(null);
            setNewTvaInput('');
            setNewClient({
                name: '',
                email: '',
                username: '',
                company_name: '',
                logo_url: '',
                tva_rates: [20],
                enable_cover_count: false,
                client_type: 'restauration',
                prestation_location_mode: 'domicile',
                modules: getModulesForClientType('restauration').map(m => ({ name: m.name, active: m.name === 'planning' }))
            });
            loadClients();
        } catch (e: any) {
            console.error(e);
            alert(e.message || 'Erreur lors de la création du client');
        }
    };

    const toggleModule = (name: string) => {
        setNewClient({
            ...newClient,
            modules: newClient.modules.map(m => m.name === name ? { ...m, active: !m.active } : m)
        });
    };

    const handleImpersonate = async (clientId: string) => {
        try {
            const data = await adminApi.impersonateClient(clientId);
            localStorage.setItem('token', data.token);
            window.location.href = '/';
        } catch (e) {
            alert('Erreur lors de la connexion virtuelle');
        }
    };

    const handleOpenFactures = async (clientRow: any) => {
        setSelectedFactureClient(clientRow);
        setFacturesLoading(true);
        try {
            const data = await adminApi.getClientFactures(clientRow.id);
            setClientFactures(Array.isArray(data) ? data : []);
        } catch (e: any) {
            console.error(e);
            setClientFactures([]);
            alert(e?.message || 'Erreur lors du chargement des factures du client');
        } finally {
            setFacturesLoading(false);
        }
    };

    const handleOpenAbsenceRequests = async (clientRow: any) => {
        setSelectedCpClient(clientRow);
        setCpLoading(true);
        try {
            const data = await adminApi.getClientAbsenceRequests(clientRow.id);
            setClientAbsenceRequests(Array.isArray(data) ? data : []);
        } catch (e: any) {
            console.error(e);
            setClientAbsenceRequests([]);
            alert(e?.message || 'Erreur lors du chargement des demandes de congé(s) du client');
        } finally {
            setCpLoading(false);
        }
    };

    const handleDeleteAbsenceRequest = async (req: any) => {
        if (!selectedCpClient?.id || !req?.id) return;
        if (!confirm(`Supprimer définitivement cette demande de congé(s) de ${req.first_name || ''} ${req.last_name || ''} ? Cette action est irréversible et retire aussi le document et l'entrée planning associés.`)) return;

        try {
            setDeletingCpId(String(req.id));
            await adminApi.deleteClientAbsenceRequest(selectedCpClient.id, String(req.id));
            setClientAbsenceRequests((current) => current.filter((item) => String(item.id) !== String(req.id)));
        } catch (e: any) {
            alert(e?.message || 'Erreur lors de la suppression de la demande');
        } finally {
            setDeletingCpId(null);
        }
    };

    const handleDeleteFacture = async (facture: any) => {
        if (!selectedFactureClient?.id || !facture?.id) return;
        if (!confirm(`Supprimer définitivement la facture ${facture.invoice_number || ''} de ${selectedFactureClient.name} ?`)) return;

        try {
            setDeletingFactureId(String(facture.id));
            await adminApi.deleteClientFacture(selectedFactureClient.id, String(facture.id));
            setClientFactures((current) => current.filter((item) => String(item.id) !== String(facture.id)));
        } catch (e: any) {
            alert(e?.message || 'Erreur lors de la suppression de la facture');
        } finally {
            setDeletingFactureId(null);
        }
    };

    // ...existing code...

    const normalizedSearch = clientSearch.trim().toLowerCase();
    const filteredClients = normalizedSearch
        ? clients.filter((c: any) => `${c.company_name || ''} ${c.name || ''} ${c.email || ''} ${c.username || ''}`.toLowerCase().includes(normalizedSearch))
        : clients;
    const restaurationClients = filteredClients.filter((c: any) => (c.client_type || 'restauration') === 'restauration');
    const prestationClients = filteredClients.filter((c: any) => c.client_type === 'prestation');

    const renderClientCard = (client: any) => (
        <motion.div
            key={client.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-surface rounded-2xl border border-border p-6 hover:border-white/20 transition-all cursor-pointer group relative overflow-hidden"
            onClick={() => navigate(`/admin/clients/${client.id}`)}
        >
            <div className="relative z-10">
                <div className="flex justify-between items-start mb-6">
                    <div className="w-12 h-12 bg-surface-soft rounded-xl flex items-center justify-center border border-border overflow-hidden">
                        {client.logo_url ? (
                            <img src={resolveLogoUrl(client.logo_url)} alt="" className="w-full h-full object-cover" />
                        ) : (
                            <Building2 className="text-content-muted" size={24} />
                        )}
                    </div>
                    <span className={`text-[10px] font-bold px-2 py-1 rounded uppercase tracking-widest ${
                        client.status === 'ACTIVE' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'
                    }`}>
                        {client.status || 'ACTIVE'}
                    </span>
                </div>
                <h3 className="text-xl font-bold text-content mb-1 group-hover:text-brand-600 dark:text-brand-400 transition-colors">{client.company_name || client.name}</h3>
                <p className="text-content-muted text-sm mb-4 truncate">{client.email}</p>
                <div className="flex items-center gap-4 text-xs text-content-muted">
                    <div className="flex items-center gap-1">
                        <ShieldCheck size={14} />
                        <span>@{client.username}</span>
                    </div>
                </div>
                <div className="mt-6 pt-4 border-t border-border flex gap-2">
                    <button
                        className="flex-1 px-3 py-2 rounded-lg bg-brand-600/10 text-brand-600 dark:text-brand-400 text-xs font-bold hover:bg-brand-600 hover:text-content transition-all"
                        onClick={e => { e.stopPropagation(); handleOpenFactures(client); }}
                    >
                        Factures
                    </button>
                    <button
                        className="flex-1 px-3 py-2 rounded-lg bg-brand-600/10 text-brand-600 dark:text-brand-400 text-xs font-bold hover:bg-brand-600 hover:text-content transition-all"
                        onClick={e => { e.stopPropagation(); handleOpenAbsenceRequests(client); }}
                    >
                        Demande de CP
                    </button>
                    <button
                        className="flex-1 px-3 py-2 rounded-lg bg-surface-soft text-content text-xs font-bold hover:bg-content hover:text-surface transition-all"
                        onClick={e => { e.stopPropagation(); handleImpersonate(client.id); }}
                    >
                        Se connecter
                    </button>
                </div>
            </div>
        </motion.div>
    );

    return (
        <div className="p-8">
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold">Gestion des Clients</h1>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => navigate('/admin/registration-requests')}
                        className="px-4 py-2 rounded-lg border border-border text-content font-bold hover:bg-surface-soft"
                    >
                        Demandes d'inscription
                    </button>
                    <button
                        onClick={() => setShowSendRegistration(true)}
                        className="px-4 py-2 rounded-lg border border-border text-content font-bold hover:bg-surface-soft"
                    >
                        Envoyer un formulaire d'inscription
                    </button>
                    <button
                        onClick={() => setShowAddModal(true)}
                        className="px-4 py-2 rounded-lg bg-surface text-content font-bold hover:bg-gray-800"
                    >
                        + Ajouter un client
                    </button>
                </div>
            </div>

            {showSendRegistration && <SendRegistrationModal onClose={() => setShowSendRegistration(false)} />}

            {!loading && clients.length > 0 && (
                <div className="relative max-w-md mb-8">
                    <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-content-muted" />
                    <input
                        type="text"
                        value={clientSearch}
                        onChange={(e) => setClientSearch(e.target.value)}
                        placeholder="Rechercher un client..."
                        className="w-full bg-surface border border-border rounded-xl pl-10 pr-4 py-2.5 text-sm text-content outline-none focus:border-brand-500 transition-colors"
                    />
                </div>
            )}

            {loading ? (
                <div className="flex justify-center py-20">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                </div>
            ) : clients.length === 0 ? (
                <div className="bg-surface rounded-2xl border border-border p-20 flex flex-col items-center justify-center text-center">
                    <Users size={40} className="text-gray-700 mb-4" />
                    <h3 className="text-xl font-bold mb-2 text-content">Aucun client</h3>
                    <p className="text-content-muted max-w-sm">Commencez par ajouter votre premier client IAmani.</p>
                </div>
            ) : filteredClients.length === 0 ? (
                <div className="bg-surface rounded-2xl border border-border p-16 flex flex-col items-center justify-center text-center">
                    <Search size={32} className="text-gray-700 mb-3" />
                    <p className="text-content-muted">Aucun client ne correspond à votre recherche.</p>
                </div>
            ) : (
                <div className="space-y-10">
                    <div>
                        <h2 className="text-sm font-bold text-content-muted uppercase tracking-widest mb-4">Restauration / Hôtellerie ({restaurationClients.length})</h2>
                        {restaurationClients.length === 0 ? (
                            <p className="text-content-muted text-sm italic">Aucun client dans cette catégorie.</p>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {restaurationClients.map(renderClientCard)}
                            </div>
                        )}
                    </div>
                    <div>
                        <h2 className="text-sm font-bold text-content-muted uppercase tracking-widest mb-4">Prestation de service ({prestationClients.length})</h2>
                        {prestationClients.length === 0 ? (
                            <p className="text-content-muted text-sm italic">Aucun client dans cette catégorie.</p>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {prestationClients.map(renderClientCard)}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ── Modal Ajouter Client ── */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-surface border border-border rounded-2xl p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold text-content">Nouveau Client</h2>
                            <button onClick={() => setShowAddModal(false)} className="text-content-muted hover:text-content"><X size={20} /></button>
                        </div>
                        <form onSubmit={handleAddClient} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-content-muted uppercase mb-1">Nom complet</label>
                                    <input value={newClient.name} onChange={e => setNewClient({...newClient, name: e.target.value})} className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-content text-sm" placeholder="Nom du contact" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-content-muted uppercase mb-1">Établissement</label>
                                    <input value={newClient.company_name} onChange={e => setNewClient({...newClient, company_name: e.target.value})} className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-content text-sm" placeholder="Brasserie / Restaurant" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-content-muted uppercase mb-2">Type de client</label>
                                <div className="grid grid-cols-2 gap-2">
                                    <button type="button" onClick={() => handleClientTypeChange('restauration')} className={`px-3 py-2.5 rounded-lg text-xs font-bold border transition-all ${newClient.client_type === 'restauration' ? 'border-brand-500/40 bg-brand-500/10 text-brand-600 dark:text-brand-400' : 'border-border text-content-muted'}`}>
                                        Restauration / Hôtellerie
                                    </button>
                                    <button type="button" onClick={() => handleClientTypeChange('prestation')} className={`px-3 py-2.5 rounded-lg text-xs font-bold border transition-all ${newClient.client_type === 'prestation' ? 'border-brand-500/40 bg-brand-500/10 text-brand-600 dark:text-brand-400' : 'border-border text-content-muted'}`}>
                                        Prestation de service
                                    </button>
                                </div>
                            </div>
                            {newClient.client_type === 'prestation' && (
                                <div>
                                    <label className="block text-xs font-bold text-content-muted uppercase mb-2">Mode d'intervention</label>
                                    <div className="grid grid-cols-3 gap-2">
                                        {([
                                            { key: 'domicile', label: 'Chez le client' },
                                            { key: 'entreprise', label: "Dans l'entreprise" },
                                            { key: 'les_deux', label: 'Les deux' },
                                        ] as const).map((opt) => (
                                            <button key={opt.key} type="button" onClick={() => setNewClient({ ...newClient, prestation_location_mode: opt.key })}
                                                className={`px-3 py-2.5 rounded-lg text-xs font-bold border transition-all ${newClient.prestation_location_mode === opt.key ? 'border-brand-500/40 bg-brand-500/10 text-brand-600 dark:text-brand-400' : 'border-border text-content-muted'}`}>
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div>
                                <label className="block text-xs font-bold text-content-muted uppercase mb-1">Email</label>
                                <input type="email" value={newClient.email} onChange={e => setNewClient({...newClient, email: e.target.value})} className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-content text-sm" placeholder="contact@example.com" required />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-content-muted uppercase mb-1">Identifiant (username)</label>
                                <input value={newClient.username} onChange={e => setNewClient({...newClient, username: e.target.value.toLowerCase()})} className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-content text-sm" placeholder="brasserie-paris" required />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-content-muted uppercase mb-1">Logo (fichier)</label>
                                <input type="file" accept="image/*" onChange={e => setNewClientLogoFile(e.target.files?.[0] || null)} className="w-full text-content-muted text-sm" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-content-muted uppercase mb-1">Taux TVA</label>
                                <div className="flex gap-2 flex-wrap mb-2">
                                    {newClient.tva_rates.map((rate, idx) => (
                                        <span key={idx} className="flex items-center gap-1 bg-slate-200 dark:bg-surface-soft rounded-full px-3 py-1 text-xs text-content">
                                            {rate}%
                                            <button type="button" onClick={() => setNewClient({...newClient, tva_rates: newClient.tva_rates.filter((_, i) => i !== idx)})} className="text-content-muted hover:text-red-400 ml-1"><X size={10} /></button>
                                        </span>
                                    ))}
                                </div>
                                <div className="flex gap-2">
                                    <input value={newTvaInput} onChange={e => setNewTvaInput(e.target.value)} type="number" className="flex-1 bg-surface border border-border rounded-lg px-3 py-2 text-content text-sm" placeholder="Ex: 10" />
                                    <button type="button" onClick={() => { const v = parseFloat(newTvaInput); if (!isNaN(v) && v >= 0) { setNewClient({...newClient, tva_rates: [...newClient.tva_rates, v]}); setNewTvaInput(''); }}} className="px-3 py-2 bg-slate-200 dark:bg-surface-soft text-content rounded-lg text-sm hover:bg-white/20">Ajouter</button>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <input type="checkbox" id="cover-count-new" checked={newClient.enable_cover_count} onChange={e => setNewClient({...newClient, enable_cover_count: e.target.checked})} className="rounded" />
                                <label htmlFor="cover-count-new" className="text-sm text-content-muted">Activer comptage couverts</label>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-content-muted uppercase mb-2">Modules actifs</label>
                                <div className="grid grid-cols-2 gap-2">
                                    {newClient.modules.map(m => (
                                        <button type="button" key={m.name} onClick={() => toggleModule(m.name)} className={`px-3 py-2 rounded-lg text-xs font-bold capitalize border ${m.active ? 'border-green-500/40 bg-green-500/10 text-green-400' : 'border-border text-content-muted'}`}>
                                            {ALL_MODULES.find(am => am.name === m.name)?.label || m.name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="flex gap-3 pt-2">
                                <button type="button" onClick={() => setShowAddModal(false)} className="flex-1 px-4 py-2 rounded-lg border border-border text-content-muted text-sm hover:text-content">Annuler</button>
                                <button type="submit" className="flex-1 px-4 py-2 rounded-lg bg-white text-slate-900 font-bold text-sm hover:bg-gray-200">Créer le client</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ── Modal Succès Création ── */}
            {successData && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-surface border border-green-500/20 rounded-2xl p-8 w-full max-w-md text-center">
                        <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                            <ShieldCheck size={32} className="text-green-400" />
                        </div>
                        <h2 className="text-xl font-bold text-content mb-2">Client créé !</h2>
                        <p className="text-content-muted text-sm mb-4">{successData.company_name || successData.name} a été ajouté avec succès.</p>
                        {successData.emailSent === false && (
                            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4 text-left">
                                <p className="text-sm font-bold text-red-400">⚠️ Email de bienvenue non envoyé</p>
                                <p className="text-xs text-content-muted mt-1">{successData.emailError || 'Erreur inconnue'} — communiquez les identifiants ci-dessous manuellement au client.</p>
                            </div>
                        )}
                        {successData.tempPassword && (
                            <div className="bg-surface rounded-xl border border-border p-4 mb-4">
                                <p className="text-xs text-content-muted mb-1">Mot de passe temporaire</p>
                                <p className="text-lg font-mono font-bold text-content">{successData.tempPassword}</p>
                                <p className="text-xs text-warning-400 mt-1">À communiquer au client et lui demander de le changer.</p>
                            </div>
                        )}
                        <button onClick={() => setSuccessData(null)} className="w-full px-4 py-2 rounded-lg bg-white text-slate-900 font-bold hover:bg-gray-200">Fermer</button>
                    </div>
                </div>
            )}

            {/* ── Modal Factures Client ── */}
            {selectedFactureClient && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-surface border border-border rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
                        <div className="flex items-center justify-between p-6 border-b border-border">
                            <div>
                                <h2 className="text-lg font-bold text-content">Factures — {selectedFactureClient.company_name || selectedFactureClient.name}</h2>
                                <p className="text-content-muted text-sm">{clientFactures.length} facture(s)</p>
                            </div>
                            <button onClick={() => { setSelectedFactureClient(null); setClientFactures([]); }} className="text-content-muted hover:text-content"><X size={20} /></button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4 space-y-3">
                            {facturesLoading ? (
                                <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white" /></div>
                            ) : clientFactures.length === 0 ? (
                                <p className="text-content-muted text-center py-10">Aucune facture pour ce client.</p>
                            ) : clientFactures.map((f: any) => (
                                <div key={f.id} className="flex items-center justify-between bg-surface rounded-xl border border-border p-4">
                                    <div>
                                        <p className="text-content font-bold">{f.invoice_number || 'Facture'}</p>
                                        <p className="text-xs text-content-muted mt-0.5">{f.customer_name} • {f.due_date ? new Date(f.due_date).toLocaleDateString() : new Date(f.created_at).toLocaleDateString()}</p>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <div className="text-right">
                                            <p className="text-content font-bold">{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(f.total_ttc ?? f.amount ?? 0))}</p>
                                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${f.status === 'paid' ? 'bg-green-500/10 text-green-400' : 'bg-orange-500/10 text-orange-400'}`}>{f.status}</span>
                                        </div>
                                        <button
                                            disabled={deletingFactureId === String(f.id)}
                                            onClick={() => handleDeleteFacture(f)}
                                            className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Modal Demande de CP Client ── */}
            {selectedCpClient && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-surface border border-border rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
                        <div className="flex items-center justify-between p-6 border-b border-border">
                            <div>
                                <h2 className="text-lg font-bold text-content">Demandes de congé(s) — {selectedCpClient.company_name || selectedCpClient.name}</h2>
                                <p className="text-content-muted text-sm">{clientAbsenceRequests.length} demande(s)</p>
                            </div>
                            <button onClick={() => { setSelectedCpClient(null); setClientAbsenceRequests([]); }} className="text-content-muted hover:text-content"><X size={20} /></button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4 space-y-3">
                            {cpLoading ? (
                                <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white" /></div>
                            ) : clientAbsenceRequests.length === 0 ? (
                                <p className="text-content-muted text-center py-10">Aucune demande de congé(s) pour ce client.</p>
                            ) : clientAbsenceRequests.map((req: any) => (
                                <div key={req.id} className="flex items-center justify-between bg-surface rounded-xl border border-border p-4">
                                    <div>
                                        <p className="text-content font-bold">{req.first_name} {req.last_name} — {req.type_code}</p>
                                        <p className="text-xs text-content-muted mt-0.5">
                                            Du {req.date_start ? new Date(req.date_start).toLocaleDateString('fr-FR') : '-'} au {req.date_end ? new Date(req.date_end).toLocaleDateString('fr-FR') : '-'} · {req.days_count || 0} jour(s)
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${req.status === 'accepted' ? 'bg-green-500/10 text-green-400' : req.status === 'refused' ? 'bg-red-500/10 text-red-400' : 'bg-orange-500/10 text-orange-400'}`}>{req.status}</span>
                                        <button
                                            disabled={deletingCpId === String(req.id)}
                                            onClick={() => handleDeleteAbsenceRequest(req)}
                                            className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const AdminClientDetailView = () => {
    const { user } = useAuth();
    const { id } = useParams();
    const navigate = useNavigate();
    const [client, setClient] = React.useState<any>(null);
    const [modules, setModules] = React.useState<any[]>([]);
    const [collaborators, setCollaborators] = React.useState<any[]>([]);
    const [spaces, setSpaces] = React.useState<any[]>([]);
    const [staffTypes, setStaffTypes] = React.useState<any[]>([]);
    const [auditLogs, setAuditLogs] = React.useState<any[]>([]);
    const [activeTab, setActiveTab] = React.useState<'collaborators' | 'spaces' | 'staff' | 'audit' | 'portail'>('collaborators');
    const [employesPortail, setEmployesPortail] = React.useState<any[]>([]);
    const [loadingPortail, setLoadingPortail] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const [showCollabModal, setShowCollabModal] = React.useState(false);
    const [showSpaceModal, setShowSpaceModal] = React.useState(false);
    const [editingCollaboratorId, setEditingCollaboratorId] = React.useState<string | null>(null);
    const [newCollab, setNewCollab] = React.useState<{ name: string; username: string; email: string; role: string; modules_access: string[]; password: string; employe_id: string | null }>({
        name: '',
        username: '',
        email: '',
        role: '',
        modules_access: ALL_MODULES.map(m => m.name),
        password: '',
        employe_id: null
    });
    const [newSpace, setNewSpace] = React.useState({ name: '', color: '#ffffff' });
    const [editLogoFile, setEditLogoFile] = React.useState<File | null>(null);
    const [editMode, setEditMode] = React.useState(false);
    const [editData, setEditData] = React.useState({
        name: '',
        email: '',
        username: '',
        company_name: '',
        logo_url: '',
        tva_rates: [] as number[],
        enable_cover_count: false,
        account_manager_first_name: '',
        account_manager_last_name: '',
        account_manager_phone: '',
        account_manager_email: '',
        legal_form: '',
        siret: '',
        vat_number: '',
        company_address: '',
        company_postal_code: '',
        company_city: '',
        company_country: 'France',
        company_employee_count: 0,
        theme_color: '#987b55',
    });
    const [editTvaInput, setEditTvaInput] = React.useState('');
    const [newStaffTypeName, setNewStaffTypeName] = React.useState('');
    const [editingStaffTypeId, setEditingStaffTypeId] = React.useState<string | null>(null);
    const [editingStaffTypeName, setEditingStaffTypeName] = React.useState('');
    const [spaceEdits, setSpaceEdits] = React.useState<Record<string, { name: string; color: string }>>({});
    const [savingSpaceId, setSavingSpaceId] = React.useState<string | null>(null);

    // ── Import Excel state ──────────────────────────────────────────────────────
    const [showImportModal, setShowImportModal] = React.useState(false);
    const [showEvenementielImportModal, setShowEvenementielImportModal] = React.useState(false);
    const [showPlanningConfig, setShowPlanningConfig] = React.useState(false);
    const [importFile, setImportFile] = React.useState<File | null>(null);
    const [evenementielImportFile, setEvenementielImportFile] = React.useState<File | null>(null);
    const [importLoading, setImportLoading] = React.useState(false);
    const [evenementielImportLoading, setEvenementielImportLoading] = React.useState(false);
    const [importError, setImportError] = React.useState<string | null>(null);
    const [evenementielImportError, setEvenementielImportError] = React.useState<string | null>(null);
    const [importPreview, setImportPreview] = React.useState<any>(null);
    const [evenementielImportPreview, setEvenementielImportPreview] = React.useState<any>(null);
    const [importResult, setImportResult] = React.useState<any>(null);
    const [evenementielImportResult, setEvenementielImportResult] = React.useState<any>(null);
    const isSuperAdmin = user?.type === 'admin';

    const handleImportFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] ?? null;
        setImportFile(file);
        setImportPreview(null);
        setImportResult(null);
        setImportError('');
        if (!file) return;
        setImportLoading(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const data = await adminApi.previewImportExcel(id!, fd);
            setImportPreview(data);
        } catch (e: any) {
            setImportError(e?.message || 'Erreur lors de l\'analyse du fichier.');
        } finally {
            setImportLoading(false);
        }
    };

    const handleExecuteImport = async () => {
        if (!importFile) return;
        setImportLoading(true);
        setImportError('');
        try {
            const fd = new FormData();
            fd.append('file', importFile);
            const result = await adminApi.executeImportExcel(id!, fd);
            setImportResult(result);
            setImportPreview(null);
        } catch (e: any) {
            setImportError(e?.message || 'Erreur lors de l\'importation.');
        } finally {
            setImportLoading(false);
        }
    };

    const handleCloseImportModal = () => {
        setShowImportModal(false);
        setImportFile(null);
        setImportPreview(null);
        setImportResult(null);
        setImportError('');
    };

    const handleCloseEvenementielImportModal = () => {
        setEvenementielImportFile(null);
        setEvenementielImportError(null);
        setEvenementielImportPreview(null);
        setEvenementielImportResult(null);
        setShowEvenementielImportModal(false);
    };

    const handleEvenementielImportFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setEvenementielImportFile(file);
        setEvenementielImportError(null);
        setEvenementielImportLoading(true);

        try {
            const formData = new FormData();
            formData.append('file', file);
            const res = await adminApi.previewImportEvenementiel(id!, formData);
            if (res.error) setEvenementielImportError(res.error);
            else setEvenementielImportPreview(res);
        } catch (e: any) {
            setEvenementielImportError(e.message);
        } finally {
            setEvenementielImportLoading(false);
        }
    };

    const executeEvenementielImport = async () => {
        if (!evenementielImportPreview) return;
        setEvenementielImportLoading(true);
        setEvenementielImportError(null);

        try {
            const res = await adminApi.executeImportEvenementiel(id!, { rows: evenementielImportPreview.rows });
            if (res.error) setEvenementielImportError(res.error);
            else setEvenementielImportResult(res);
        } catch (e: any) {
            setEvenementielImportError(e.message);
        } finally {
            setEvenementielImportLoading(false);
        }
    };

    React.useEffect(() => {
        if (id) loadData();
    }, [id]);

    const loadData = async () => {
        try {
            const [currentClient, m, c, s, logs] = await Promise.all([
                adminApi.getClient(id!),
                adminApi.getClientModules(id!),
                adminApi.getCollaborators(id!),
                adminApi.getSpaces(id!),
                adminApi.getClientAuditLogs(id!)
            ]);
            const staff = await adminApi.getStaffTypes(id!);
            setClient(currentClient);
            const parsedRates = (() => {
                try {
                    const raw = currentClient.tva_rates;
                    const arr = Array.isArray(raw) ? raw : (typeof raw === 'string' && raw !== '[object Object]' ? JSON.parse(raw || '[]') : []);
                    if (Array.isArray(arr) && arr.length > 0) return arr.map(Number).filter((n: number) => Number.isFinite(n) && n >= 0);
                } catch {}
                return [20];
            })();
            const hasExplicitManagerName = Boolean(String(currentClient.account_manager_first_name || '').trim() || String(currentClient.account_manager_last_name || '').trim());
            const normalizedManagerPhone = String(currentClient.account_manager_phone || '').trim();
            const normalizedManagerEmail = String(currentClient.account_manager_email || '').trim().toLowerCase();
            const normalizedCompanyPhone = String(currentClient.company_phone || '').trim();
            const normalizedCompanyEmail = String(currentClient.company_email || '').trim().toLowerCase();
            const shouldHideAutoManagerPhone = !hasExplicitManagerName && normalizedManagerPhone && normalizedManagerPhone === normalizedCompanyPhone;
            const shouldHideAutoManagerEmail = !hasExplicitManagerName && normalizedManagerEmail && normalizedManagerEmail === normalizedCompanyEmail;

            setEditData({
                name: currentClient.name,
                email: currentClient.email,
                username: currentClient.username || '',
                company_name: currentClient.company_name || currentClient.name || '',
                logo_url: currentClient.logo_url || '',
                tva_rates: parsedRates,
                enable_cover_count: Boolean(currentClient.enable_cover_count),
                account_manager_first_name: currentClient.account_manager_first_name || '',
                account_manager_last_name: currentClient.account_manager_last_name || '',
                account_manager_phone: shouldHideAutoManagerPhone ? '' : (currentClient.account_manager_phone || ''),
                account_manager_email: shouldHideAutoManagerEmail ? '' : (currentClient.account_manager_email || ''),
                legal_form: currentClient.legal_form || '',
                siret: currentClient.siret || '',
                vat_number: currentClient.vat_number || '',
                company_address: currentClient.company_address || '',
                company_postal_code: currentClient.company_postal_code || '',
                company_city: currentClient.company_city || '',
                company_country: currentClient.company_country || 'France',
                company_employee_count: Number(currentClient.company_employee_count || 0),
                theme_color: currentClient.theme_color || '#987b55',
            });
            setModules(m || []);
            setCollaborators(c || []);
            setSpaces(s || []);
            setStaffTypes(staff || []);
            setAuditLogs(logs || []);
            const edits: Record<string, { name: string; color: string }> = {};
            (s || []).forEach((sp: any) => {
                edits[sp.id] = { name: String(sp.name || ''), color: String(sp.color || '#ffffff') };
            });
            setSpaceEdits(edits);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateModules = async (moduleName: string, active: boolean) => {
        try {
            // "Demande de congé(s)" ne peut jamais être actif sans "Portail salarié"
            if (moduleName === 'demande_conges' && active) {
                const portailActive = modules.find(rm => rm.module_name === 'portail_salarie')?.is_active === 1;
                if (!portailActive) {
                    alert('Le module "Portail salarié" doit être activé avant "Demande de congé(s)".');
                    return;
                }
            }
            if (moduleName === 'portail_salarie' && !active) {
                const demandeCongesActive = modules.find(rm => rm.module_name === 'demande_conges')?.is_active === 1;
                if (demandeCongesActive && !confirm('Désactiver "Portail salarié" désactivera aussi "Demande de congé(s)". Continuer ?')) {
                    return;
                }
            }

            // Rebuild the modules list from les modules applicables au type de ce client
            const applicableModules = getModulesForClientType(client?.client_type);
            const updatedModules = applicableModules.map(am => {
                const existing = modules.find(rm => rm.module_name === am.name);
                if (am.name === moduleName) {
                    return { ...existing, module_name: am.name, is_active: active ? 1 : 0 };
                }
                if (am.name === 'demande_conges' && moduleName === 'portail_salarie' && !active) {
                    return { ...existing, module_name: am.name, is_active: 0 };
                }
                return existing || { module_name: am.name, is_active: 0 };
            });

            await adminApi.updateClientModules(id!, updatedModules.map(m => ({
                name: m.module_name,
                active: m.is_active === 1
            })));
            setModules(updatedModules);
        } catch (e) {
            alert('Erreur lors de la mise à jour des modules');
        }
    };

    const handleUpdateStatus = async (status: string) => {
        try {
            await adminApi.updateClient(id!, { status });
            setClient({ ...client, status });
        } catch (e) {
            alert('Erreur lors de la mise à jour du statut');
        }
    };

    const handleUpdateProfile = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            let nextLogoUrl = editData.logo_url;
            if (editLogoFile) {
                // Redimensionné/recompressé côté navigateur avant envoi — l'API refuse tout logo dépassant
                // 1 Mo et une image un peu détaillée (dégradés, texte) dépasse très vite cette limite en
                // résolution d'origine, faisant échouer l'upload sans que ce soit évident pour l'utilisateur.
                const { base64: logoBase64, mimeType: logoMimeType } = await resizeImageForUpload(editLogoFile);
                const upload = await adminApi.uploadLogo(logoBase64, logoMimeType);
                if (!upload.logo_url) {
                    throw new Error(upload.error || 'Erreur upload logo');
                }
                nextLogoUrl = upload.logo_url;
            }

            const payload = { ...editData, logo_url: nextLogoUrl };
            await adminApi.updateClient(id!, payload);
            setClient({ ...client, ...payload });
            setEditData((prev) => ({ ...prev, logo_url: nextLogoUrl }));
            setEditLogoFile(null);
            setEditMode(false);
        } catch (e: any) {
            alert('Erreur lors de la mise à jour du profil: ' + (e?.message || String(e)));
        }
    };

    const handleDeleteClient = async () => {
        if (!confirm('Êtes-vous sûr de vouloir supprimer ce client ? Cette action est irréversible et supprimera TOUTES les données associées.')) return;
        try {
            await adminApi.deleteClient(id!);
            navigate('/admin/clients');
        } catch (e) {
            alert('Erreur lors de la suppression');
        }
    };

    const handleResetPassword = async () => {
        if (!confirm('Renvoyer un nouveau mot de passe temporaire au client ?')) return;
        try {
            await adminApi.resetClientPassword(id!);
            alert('Un nouveau mot de passe a été envoyé par email.');
        } catch (e) {
            alert('Erreur lors de la réinitialisation');
        }
    };

    const handleForceReset = async () => {
        if (!confirm('Générer un lien de réinitialisation et l\'envoyer au client ?')) return;
        try {
            await adminApi.forceResetClientPassword(id!);
            alert('Un lien de réinitialisation a été envoyé par email.');
        } catch (e) {
            alert('Erreur lors de l\'envoi du lien');
        }
    };

    const handleResetCollaboratorPassword = async (collab: any) => {
        if (!confirm(`Renvoyer de nouveaux identifiants temporaires à ${collab.name || 'ce collaborateur'} ?`)) return;
        try {
            await adminApi.resetCollaboratorPassword(id!, collab.id);
            alert('Les nouveaux identifiants ont été envoyés par email au collaborateur.');
        } catch (e: any) {
            alert(e?.message || 'Erreur lors de la réinitialisation du collaborateur');
        }
    };

    const handleForceResetCollaborator = async (collab: any) => {
        if (!confirm(`Forcer la réinitialisation du mot de passe de ${collab.name || 'ce collaborateur'} ?`)) return;
        try {
            await adminApi.forceResetCollaboratorPassword(id!, collab.id);
            alert('La réinitialisation du mot de passe a été forcée et envoyée par email.');
        } catch (e: any) {
            alert(e?.message || 'Erreur lors de l\'envoi du lien de réinitialisation');
        }
    };

    const handleAddCollab = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (editingCollaboratorId) {
                await adminApi.updateCollaborator(id!, editingCollaboratorId, newCollab);
            } else {
                await adminApi.createCollaborator(id!, newCollab);
            }
            setShowCollabModal(false);
            setEditingCollaboratorId(null);
            setNewCollab({ name: '', username: '', email: '', role: '', modules_access: ['planning'], password: '', employe_id: null });
            loadData();
        } catch (e: any) {
            alert(e?.message || 'Erreur lors de l\'enregistrement du collaborateur');
        }
    };

    const ensureEmployesPortailLoaded = async () => {
        if (employesPortail.length > 0) return;
        setLoadingPortail(true);
        try {
            const data = await apiFetch(`/admin/clients/${id}/employes-portail`);
            setEmployesPortail(Array.isArray(data) ? data : []);
        } finally { setLoadingPortail(false); }
    };

    const handleOpenCreateCollab = () => {
        setEditingCollaboratorId(null);
        setNewCollab({ name: '', username: '', email: '', role: '', modules_access: getModulesForClientType(client?.client_type).map(m => m.name), password: '', employe_id: null });
        setShowCollabModal(true);
        ensureEmployesPortailLoaded();
    };

    const handleOpenEditCollab = (collab: any) => {
        let parsedModules: string[] = [];
        try {
            const raw = collab?.modules_access;
            if (Array.isArray(raw)) {
                parsedModules = raw.map((m: any) => String(m));
            } else if (typeof raw === 'string') {
                const decoded = JSON.parse(raw || '[]');
                parsedModules = Array.isArray(decoded) ? decoded.map((m: any) => String(m)) : [];
            }
        } catch {
            parsedModules = [];
        }
        setEditingCollaboratorId(String(collab.id));
        setNewCollab({
            name: String(collab.name || ''),
            username: String(collab.username || ''),
            email: String(collab.email || ''),
            role: String(collab.role || ''),
            modules_access: getModulesForClientType(client?.client_type).filter(m => parsedModules.includes(m.name)).map(m => m.name),
            password: '',
            employe_id: collab.employe_id ? String(collab.employe_id) : null
        });
        setShowCollabModal(true);
        ensureEmployesPortailLoaded();
    };

    const handleDeleteCollaborator = async (collaboratorId: string) => {
        if (!confirm('Supprimer ce collaborateur définitivement ? Cette action est irréversible.')) return;
        try {
            await adminApi.deleteCollaborator(id!, collaboratorId);
            loadData();
        } catch (e: any) {
            alert(e?.message || 'Erreur lors de la suppression du collaborateur');
        }
    };

    const toggleCollaboratorModule = (moduleName: string) => {
        setNewCollab((prev) => {
            const hasModule = prev.modules_access.includes(moduleName);
            return {
                ...prev,
                modules_access: hasModule
                    ? prev.modules_access.filter((m) => m !== moduleName)
                    : [...prev.modules_access, moduleName]
            };
        });
    };

    const handleAddSpace = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await adminApi.createSpace(id!, newSpace);
            setShowSpaceModal(false);
            setNewSpace({ name: '', color: '#ffffff' });
            loadData();
        } catch (e) {
            alert('Erreur lors de la création de l\'espace');
        }
    };

    const handleDeleteSpace = async (spaceId: string) => {
        if (!confirm('Supprimer cet espace ?')) return;
        try {
            await adminApi.deleteSpace(id!, spaceId);
            loadData();
        } catch (e) {
            alert('Erreur lors de la suppression');
        }
    };

    const handleSaveSpace = async (spaceId: string) => {
        const edit = spaceEdits[spaceId];
        if (!edit) return;
        if (!edit.name.trim()) { alert("Le nom de l'espace est requis."); return; }
        setSavingSpaceId(spaceId);
        try {
            await adminApi.updateSpace(id!, spaceId, { name: edit.name.trim(), color: edit.color });
            loadData();
        } catch (e) {
            alert("Erreur lors de la sauvegarde de l'espace");
        } finally {
            setSavingSpaceId(null);
        }
    };

    if (loading) return <div className="text-center py-20 text-content-muted">Chargement...</div>;
    if (!client) return <div className="text-center py-20 text-content-muted">Client introuvable</div>;

    const handleAddStaffType = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        if (!newStaffTypeName.trim()) return;
        if (!id) {
            console.error('No client ID found in URL parameters');
            alert('ID client manquant. Veuillez rafraîchir la page.');
            return;
        }

        try {
            console.log('Creating staff type for client:', id, 'name:', newStaffTypeName.trim());
            await adminApi.createStaffType(id, { name: newStaffTypeName.trim() });
            setNewStaffTypeName('');
            await loadData();
            console.log('Staff type created and data reloaded');
        } catch (e: any) {
            console.error('Error creating staff type:', e);
            alert(e?.message || 'Erreur lors de la création de la catégorie.');
        }
    };

    const handleEditStaffType = (type: any) => {
        setEditingStaffTypeId(type.id);
        setEditingStaffTypeName(String(type.name || ''));
    };

    const handleSaveStaffType = async () => {
        if (!editingStaffTypeId || !editingStaffTypeName.trim()) return;
        try {
            await adminApi.createStaffType(id!, { id: editingStaffTypeId, name: editingStaffTypeName.trim() });
            setEditingStaffTypeId(null);
            setEditingStaffTypeName('');
            loadData();
        } catch (e: any) {
            alert(e?.message || 'Erreur lors de la modification.');
        }
    };

    const handleDeleteStaffType = async (staffId: string) => {
        if (!confirm('Supprimer cette catégorie de staff ?')) return;
        try {
            await adminApi.deleteStaffType(id!, staffId);
            loadData();
        } catch (e: any) {
            alert(e?.message || 'Erreur lors de la suppression.');
        }
    };

    const displayValue = (value: any, fallback = 'Non renseigné') => {
        const text = String(value ?? '').trim();
        return text || fallback;
    };

    const companyAddressLine = [
        client.company_address,
        [client.company_postal_code, client.company_city].filter(Boolean).join(' '),
        client.company_country,
    ].map((v: any) => String(v || '').trim()).filter(Boolean).join(', ');

    const legalDetailsLine = [
        client.capital ? `Capital ${client.capital}` : '',
        client.ape ? `APE ${client.ape}` : '',
        client.rcs_ville && client.rcs_numero ? `RCS ${client.rcs_ville} ${client.rcs_numero}` : '',
    ].filter(Boolean).join(' • ');

    const managerHasIdentity = Boolean(String(client.account_manager_first_name || '').trim() || String(client.account_manager_last_name || '').trim());
    const managerPhoneValue = !managerHasIdentity && String(client.account_manager_phone || '').trim() === String(client.company_phone || '').trim()
        ? ''
        : String(client.account_manager_phone || '').trim();
    const managerEmailValue = !managerHasIdentity && String(client.account_manager_email || '').trim().toLowerCase() === String(client.company_email || '').trim().toLowerCase()
        ? ''
        : String(client.account_manager_email || '').trim();

    const statBadges = [
        { label: 'Employés saisis', value: Number(client.employees_count || 0) },
        { label: 'Collaborateurs', value: Number(client.collaborators_count || 0) },
        { label: 'Calendriers', value: Number(client.calendars_count || 0) },
        { label: 'Modules actifs', value: Number(client.active_modules_count || 0) },
    ];

    if (showPlanningConfig) {
        return (
            <div className="min-h-screen bg-[var(--bg-app)] p-4 md:p-8">
                <AdminPlanningConfig 
                    clientId={client.id} 
                    clientName={client.company_name || client.name} 
                    onBack={() => setShowPlanningConfig(false)} 
                />
            </div>
        );
    }

    return (
        <div className="space-y-10">
            <header className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                    <Link to="/admin/clients" className="text-sm text-content-muted hover:text-content flex items-center gap-1 mb-2 transition-all">
                        <ChevronRight size={14} className="rotate-180" /> Retour aux clients
                    </Link>
                    <div className="flex items-center gap-4">
                        <h1 className="text-3xl font-bold tracking-tight text-content">{client.name}</h1>
                        <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest ${
                            client.status === 'active' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'
                        }`}>
                            {client.status === 'active' ? 'Actif' : 'Bloqué'}
                        </span>
                    </div>
                    <p className="text-content-muted mt-1">ID: {client.id} • Email: {client.email}</p>
                </div>
                <div className="flex flex-wrap gap-3">
                    <button 
                        onClick={() => handleUpdateStatus(client.status === 'active' ? 'blocked' : 'active')}
                        className={`px-4 py-2 rounded-lg font-bold text-sm transition-all flex items-center gap-2 ${
                            client.status === 'active' 
                                ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20' 
                                : 'bg-green-500/10 text-green-400 hover:bg-green-500/20'
                        }`}
                    >
                        {client.status === 'active' ? <Lock size={16} /> : <ShieldCheck size={16} />}
                        {client.status === 'active' ? 'Bloquer le compte' : 'Débloquer le compte'}
                    </button>
                    <button 
                        onClick={handleResetPassword}
                        className="bg-surface-soft border border-border text-content px-4 py-2 rounded-lg font-bold text-sm hover:bg-slate-200 dark:bg-surface-soft transition-all flex items-center gap-2"
                        title="Renvoyer un mot de passe temporaire"
                    >
                        <Mail size={16} />
                        Renvoyer Identifiants
                    </button>
                    <button 
                        onClick={handleForceReset}
                        className="bg-surface-soft border border-border text-content px-4 py-2 rounded-lg font-bold text-sm hover:bg-slate-200 dark:bg-surface-soft transition-all flex items-center gap-2"
                        title="Envoyer un lien de réinitialisation"
                    >
                        <ShieldCheck size={16} />
                        Forcer Reset MDP
                    </button>
                    <button 
                        onClick={() => setEditMode(true)}
                        className="bg-surface-soft border border-border text-content px-4 py-2 rounded-lg font-bold text-sm hover:bg-slate-200 dark:bg-surface-soft transition-all flex items-center gap-2"
                    >
                        <Settings size={16} />
                        Éditer Profil
                    </button>
                    <button 
                        onClick={handleDeleteClient}
                        className="bg-red-500/10 text-red-400 px-4 py-2 rounded-lg font-bold text-sm hover:bg-red-500/20 transition-all flex items-center gap-2"
                    >
                        <Trash2 size={16} />
                        Supprimer
                    </button>
                </div>
            </header>

            <div className="space-y-6">
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                    <div className="bg-surface rounded-2xl p-6 md:p-7 border border-border">
                        <div className="flex items-start justify-between gap-4 mb-5">
                            <div>
                                <h2 className="text-xl font-bold text-content flex items-center gap-2">
                                    <ShieldCheck size={20} className="text-cyan-400" />
                                    Contact gestionnaire
                                </h2>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div className="rounded-xl bg-surface border border-border p-4">
                                <p className="text-[11px] uppercase tracking-widest text-content-muted mb-1">Nom</p>
                                <p className="text-content font-semibold">{displayValue([client.account_manager_first_name, client.account_manager_last_name].filter(Boolean).join(' '))}</p>
                            </div>
                            <div className="rounded-xl bg-surface border border-border p-4">
                                <p className="text-[11px] uppercase tracking-widest text-content-muted mb-1 flex items-center gap-2"><Phone size={13} />Téléphone</p>
                                <p className="text-content font-semibold">{displayValue(managerPhoneValue)}</p>
                            </div>
                            <div className="rounded-xl bg-surface border border-border p-4 md:col-span-2">
                                <p className="text-[11px] uppercase tracking-widest text-content-muted mb-1 flex items-center gap-2"><Mail size={13} />Adresse email</p>
                                <p className="text-content font-semibold break-all">{displayValue(managerEmailValue)}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-surface rounded-2xl p-6 md:p-7 border border-border">
                        <div className="flex items-start justify-between gap-4 mb-5">
                            <div>
                                <h2 className="text-xl font-bold text-content flex items-center gap-2">
                                    <Building2 size={20} className="text-brand-600 dark:text-brand-400" />
                                    Fiche entreprise
                                </h2>
                                <p className="text-sm text-content-muted mt-1">Vue synthétique, claire et modifiable à tout moment.</p>
                            </div>
                            <button
                                onClick={() => setEditMode(true)}
                                className="px-3 py-1.5 rounded-lg text-xs font-bold bg-surface-soft border border-border text-content hover:bg-slate-200 dark:bg-surface-soft transition-all"
                            >
                                Mettre à jour
                            </button>
                        </div>
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                <div className="rounded-xl bg-surface border border-border p-4">
                                    <p className="text-[11px] uppercase tracking-widest text-content-muted mb-1">Nom commercial</p>
                                    <p className="text-content font-semibold">{displayValue(client.company_name || client.name)}</p>
                                </div>
                                <div className="rounded-xl bg-surface border border-border p-4">
                                    <p className="text-[11px] uppercase tracking-widest text-content-muted mb-1">Forme juridique</p>
                                    <p className="text-content font-semibold">{displayValue(client.legal_form)}</p>
                                </div>
                                <div className="rounded-xl bg-surface border border-border p-4 md:col-span-2">
                                    <p className="text-[11px] uppercase tracking-widest text-content-muted mb-1 flex items-center gap-2"><MapPin size={13} />Adresse postale</p>
                                    <p className="text-content font-semibold">{displayValue(companyAddressLine)}</p>
                                </div>
                                <div className="rounded-xl bg-surface border border-border p-4">
                                    <p className="text-[11px] uppercase tracking-widest text-content-muted mb-1 flex items-center gap-2"><Phone size={13} />Téléphone entreprise</p>
                                    <p className="text-content font-semibold">{displayValue(client.company_phone)}</p>
                                </div>
                                <div className="rounded-xl bg-surface border border-border p-4">
                                    <p className="text-[11px] uppercase tracking-widest text-content-muted mb-1 flex items-center gap-2"><Mail size={13} />Email entreprise</p>
                                    <p className="text-content font-semibold break-all">{displayValue(client.company_email)}</p>
                                </div>
                                <div className="rounded-xl bg-surface border border-border p-4">
                                    <p className="text-[11px] uppercase tracking-widest text-content-muted mb-1">SIRET</p>
                                    <p className="text-content font-semibold">{displayValue(client.siret)}</p>
                                </div>
                                <div className="rounded-xl bg-surface border border-border p-4">
                                    <p className="text-[11px] uppercase tracking-widest text-content-muted mb-1">TVA intracom</p>
                                    <p className="text-content font-semibold">{displayValue(client.vat_number)}</p>
                                </div>
                                <div className="rounded-xl bg-surface border border-border p-4 md:col-span-2">
                                    <p className="text-[11px] uppercase tracking-widest text-content-muted mb-1">Mentions légales</p>
                                    <p className="text-content font-semibold">{displayValue(legalDetailsLine)}</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {statBadges.map((item) => (
                                    <div key={item.label} className="rounded-xl bg-surface-soft border border-border p-3 text-center">
                                        <p className="text-xl font-bold text-content">{item.value}</p>
                                        <p className="text-[11px] text-content-muted mt-1">{item.label}</p>
                                    </div>
                                ))}
                            </div>

                            <div className="rounded-xl bg-surface border border-border p-4 text-sm">
                                <div className="flex justify-between gap-3">
                                    <span className="text-content-muted">Effectif déclaré</span>
                                    <span className="text-content font-semibold">{Number(client.company_employee_count || 0)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="bg-surface rounded-2xl p-4 md:p-8 border border-border">
                        <h2 className="text-xl font-bold text-content mb-6 flex items-center gap-2">
                            <Settings size={20} className="text-purple-400" />
                            Provisioning
                        </h2>
                        <div className="space-y-4">
                            {getModulesForClientType(client?.client_type).map((m) => {
                                const isActive = modules.find(rm => rm.module_name === m.name)?.is_active === 1;
                                const portailActive = modules.find(rm => rm.module_name === 'portail_salarie')?.is_active === 1;
                                const disabled = m.name === 'demande_conges' && !isActive && !portailActive;
                                return (
                                    <div
                                        key={m.name}
                                        className={`flex items-center justify-between p-4 rounded-xl bg-surface border border-border ${disabled ? 'opacity-50' : ''}`}
                                        title={disabled ? 'Activez "Portail salarié" en premier' : undefined}
                                    >
                                        <span className="capitalize font-medium text-content">{m.label}</span>
                                        <button
                                            onClick={() => !disabled && handleUpdateModules(m.name, !isActive)}
                                            disabled={disabled}
                                            className="transition-all disabled:cursor-not-allowed"
                                        >
                                            {isActive ? <ToggleRight size={32} className="text-content" /> : <ToggleLeft size={32} className="text-gray-700" />}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="bg-surface rounded-2xl p-4 md:p-8 border border-border">
                        <h2 className="text-xl font-bold text-content mb-6 flex items-center gap-2">
                            <ShieldCheck size={20} className="text-brand-600 dark:text-brand-400" />
                            Audit & Sécurité
                        </h2>
                        <div className="space-y-4 text-sm">
                            <div className="flex justify-between py-2 border-b border-border">
                                <span className="text-content-muted">Dernière Connexion</span>
                                <span className="text-content font-medium">{client.last_login ? new Date(client.last_login).toLocaleString() : 'Jamais'}</span>
                            </div>
                            <div className="flex justify-between py-2 border-b border-border">
                                <span className="text-content-muted">Date de Création</span>
                                <span className="text-content font-medium">{new Date(client.created_at).toLocaleDateString()}</span>
                            </div>
                            <div className="flex justify-between py-2">
                                <span className="text-content-muted">Type de Compte</span>
                                <span className="text-content font-medium uppercase tracking-widest text-[10px] bg-surface-soft px-2 py-0.5 rounded">Client SaaS</span>
                            </div>
                        </div>
                    </div>

                    {/* Import Excel/PDF (SuperAdmin only) */}
                    {isSuperAdmin && (
                    <div className="bg-surface rounded-2xl p-4 md:p-8 border border-border space-y-4">
                        <div>
                            <h2 className="text-xl font-bold text-content mb-2 flex items-center gap-2">
                                <FileSpreadsheet size={20} className="text-green-400" />
                                Import RH (Employés)
                            </h2>
                            <p className="text-sm text-content-muted mb-4">Importer des employés et postes en masse (.xlsx).</p>
                            <button
                                onClick={() => setShowImportModal(true)}
                                className="w-full bg-green-500/10 border border-green-500/30 text-green-400 py-3 rounded-xl font-bold hover:bg-green-500/20 transition-all flex items-center justify-center gap-2"
                            >
                                <Upload size={16} />
                                Configurer RH
                            </button>
                        </div>
                        
                        <div className="pt-4 border-t border-border">
                            <h2 className="text-xl font-bold text-content mb-2 flex items-center gap-2">
                                <Calendar size={20} className="text-brand-600 dark:text-brand-400" />
                                Import Événementiel
                            </h2>
                            <p className="text-sm text-content-muted mb-4">Importer des privatisations, clients CRM et staff en masse.</p>
                            <button
                                onClick={() => setShowEvenementielImportModal(true)}
                                className="w-full bg-brand-500/10 border border-brand-500/30 text-brand-600 dark:text-brand-400 py-3 rounded-xl font-bold hover:bg-brand-500/20 transition-all flex items-center justify-center gap-2"
                            >
                                <Upload size={16} />
                                Importer Événements
                            </button>
                        </div>

                        <div className="pt-4 border-t border-border">
                            <h2 className="text-xl font-bold text-content mb-2 flex items-center gap-2">
                                <Clock size={20} className="text-orange-400" />
                                Config Planning
                            </h2>
                            <p className="text-sm text-content-muted mb-4">Gérer les codes d'absence et types de renfort.</p>
                            <button
                                onClick={() => setShowPlanningConfig(true)}
                                className="w-full bg-orange-500/10 border border-orange-500/30 text-orange-400 py-3 rounded-xl font-bold hover:bg-orange-500/20 transition-all flex items-center justify-center gap-2"
                            >
                                <Settings size={16} />
                                Configurer Planning
                            </button>
                        </div>
                    </div>
                    )}
                </div>

                <div className="space-y-6">
                    <div className="flex gap-2 bg-surface rounded-xl p-1 border border-border w-fit">
                        <button
                            onClick={() => setActiveTab('collaborators')}
                            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${activeTab === 'collaborators' ? 'bg-white text-slate-900' : 'text-content-muted hover:text-content'}`}
                        >
                            Collaborateurs
                        </button>
                        <button
                            onClick={() => setActiveTab('spaces')}
                            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${activeTab === 'spaces' ? 'bg-white text-slate-900' : 'text-content-muted hover:text-content'}`}
                        >
                            Espaces
                        </button>
                        <button
                            onClick={() => setActiveTab('staff')}
                            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${activeTab === 'staff' ? 'bg-white text-slate-900' : 'text-content-muted hover:text-content'}`}
                        >
                            Staff
                        </button>
                        <button
                            onClick={async () => {
                                setActiveTab('portail');
                                if (employesPortail.length === 0) {
                                    setLoadingPortail(true);
                                    try {
                                        const data = await apiFetch(`/admin/clients/${id}/employes-portail`);
                                        setEmployesPortail(Array.isArray(data) ? data : []);
                                    } finally { setLoadingPortail(false); }
                                }
                            }}
                            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${activeTab === 'portail' ? 'bg-brand-600 text-brand-fg' : 'text-content-muted hover:text-content'}`}
                        >
                            👥 Portail Salariés
                        </button>
                    </div>

                    {activeTab === 'collaborators' && (
                    <div className="bg-surface rounded-2xl p-4 md:p-8 border border-border">
                        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
                            <h2 className="text-xl font-bold text-content flex items-center gap-2">
                                <Users size={20} className="text-green-400" />
                                Collaborateurs
                            </h2>
                            <button 
                                onClick={handleOpenCreateCollab}
                                className="text-sm font-bold text-content hover:text-content-muted flex items-center gap-2 transition-all"
                            >
                                <Plus size={16} /> Ajouter
                            </button>
                        </div>

                        <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
                            <table className="w-full text-left min-w-[640px]">
                                <thead>
                                    <tr className="text-xs font-bold text-content-muted uppercase tracking-widest border-b border-border">
                                        <th className="pb-4">Nom</th>
                                        <th className="pb-4">Pseudo</th>
                                        <th className="pb-4">Email</th>
                                        <th className="pb-4">Rôle</th>
                                        <th className="pb-4 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-white/5">
                                    {collaborators.length === 0 ? (
                                        <tr><td colSpan={5} className="py-10 text-center text-content-muted">Aucun collaborateur.</td></tr>
                                    ) : collaborators.map((collab) => (
                                        <tr key={collab.id}>
                                            <td className="py-4 font-bold text-content">{collab.name}</td>
                                            <td className="py-4 text-content-muted font-mono text-sm">{collab.username || <span className="text-gray-600 italic">—</span>}</td>
                                            <td className="py-4 text-content-muted">{collab.email || <span className="text-gray-600 italic">—</span>}</td>
                                            <td className="py-4">
                                                <span className="text-[10px] font-bold uppercase tracking-widest bg-surface-soft px-2 py-1 rounded text-content-muted">
                                                    {collab.role || 'Collaborateur'}
                                                </span>
                                            </td>
                                            <td className="py-4 text-right">
                                                <div className="flex items-center justify-end gap-2 flex-wrap">
                                                    {isSuperAdmin && (
                                                        <>
                                                            <button
                                                                onClick={() => handleResetCollaboratorPassword(collab)}
                                                                className="text-xs font-bold px-2.5 py-1 rounded-lg bg-brand-500/10 text-emerald-400 hover:bg-brand-500/20 transition-all"
                                                            >
                                                                Identifiants
                                                            </button>
                                                            <button
                                                                onClick={() => handleForceResetCollaborator(collab)}
                                                                className="text-xs font-bold px-2.5 py-1 rounded-lg bg-warning-500/10 text-warning-400 hover:bg-warning-500/20 transition-all"
                                                            >
                                                                Forcer MDP
                                                            </button>
                                                        </>
                                                    )}
                                                    <button
                                                        onClick={() => handleOpenEditCollab(collab)}
                                                        className="text-content-muted hover:text-content transition-all text-sm"
                                                    >
                                                        Modifier
                                                    </button>
                                                    <button onClick={() => handleDeleteCollaborator(collab.id)} className="text-gray-600 hover:text-red-500 transition-all">
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    )}

                    {activeTab === 'staff' && (
                    <div className="bg-surface rounded-2xl p-4 md:p-8 border border-border">
                        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
                            <h2 className="text-xl font-bold text-content flex items-center gap-2">
                                <Briefcase size={20} className="text-orange-400" />
                                Catégories de Staff
                            </h2>
                            <form onSubmit={handleAddStaffType} className="flex items-center gap-2 flex-wrap">
                                <input
                                    value={newStaffTypeName}
                                    onChange={(e) => setNewStaffTypeName(e.target.value)}
                                    placeholder="Nouvelle catégorie"
                                    className="bg-surface border border-border rounded-lg px-3 py-2 text-content text-sm outline-none focus:border-border w-full sm:w-48"
                                />
                                <button
                                    type="submit"
                                    disabled={!newStaffTypeName.trim()}
                                    className="text-sm font-bold text-content hover:text-content-muted flex items-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Plus size={16} /> Ajouter une catégorie
                                </button>
                            </form>
                        </div>

                        <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
                            <table className="w-full text-left min-w-[420px]">
                                <thead>
                                    <tr className="text-xs font-bold text-content-muted uppercase tracking-widest border-b border-border">
                                        <th className="pb-4">Nom</th>
                                        <th className="pb-4 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-white/5">
                                    {staffTypes.length === 0 ? (
                                        <tr><td colSpan={2} className="py-10 text-center text-content-muted">Aucune catégorie de staff.</td></tr>
                                    ) : staffTypes.map((type: any) => (
                                        <tr key={type.id} className="bg-[var(--bg-app)]">
                                            <td className="py-4 text-content font-medium">
                                                {editingStaffTypeId === type.id ? (
                                                    <input
                                                        value={editingStaffTypeName}
                                                        onChange={(e) => setEditingStaffTypeName(e.target.value)}
                                                        className="bg-surface border border-border rounded-lg px-3 py-2 text-content text-sm outline-none focus:border-border w-full max-w-md"
                                                    />
                                                ) : (
                                                    type.name
                                                )}
                                            </td>
                                            <td className="py-4 text-right">
                                                {editingStaffTypeId === type.id ? (
                                                    <div className="inline-flex gap-3">
                                                        <button onClick={handleSaveStaffType} className="text-content-muted hover:text-content text-sm">Enregistrer</button>
                                                        <button onClick={() => { setEditingStaffTypeId(null); setEditingStaffTypeName(''); }} className="text-gray-600 hover:text-content-muted text-sm">Annuler</button>
                                                    </div>
                                                ) : (
                                                    <div className="inline-flex gap-3">
                                                        <button onClick={() => handleEditStaffType(type)} className="text-content-muted hover:text-content text-sm">Modifier</button>
                                                        <button onClick={() => handleDeleteStaffType(type.id)} className="text-gray-600 hover:text-red-500 transition-all">
                                                            <Trash2 size={16} />
                                                        </button>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    )}

                    {activeTab === 'spaces' && (
                    <div className="bg-surface rounded-2xl p-8 border border-border">
                        <div className="flex items-center justify-between mb-8">
                            <h2 className="text-xl font-bold text-content flex items-center gap-2">
                                <MapPin size={20} className="text-orange-400" />
                                Espaces
                            </h2>
                            <button 
                                onClick={() => setShowSpaceModal(true)}
                                className="text-sm font-bold text-content hover:text-content-muted flex items-center gap-2 transition-all"
                            >
                                <Plus size={16} /> Ajouter un espace
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {spaces.length === 0 ? (
                                <p className="col-span-full py-10 text-center text-content-muted">Aucun espace configuré.</p>
                            ) : spaces.map((space) => (
                                <div key={space.id} className="flex items-center gap-3 p-4 rounded-xl bg-surface border border-border">
                                    <input
                                        type="color"
                                        value={spaceEdits[space.id]?.color || space.color}
                                        onChange={(e) => setSpaceEdits(prev => ({ ...prev, [space.id]: { ...prev[space.id], color: e.target.value } }))}
                                        className="w-8 h-8 rounded cursor-pointer border-0 bg-transparent flex-shrink-0"
                                        title="Changer la couleur"
                                    />
                                    <input
                                        type="text"
                                        value={spaceEdits[space.id]?.name ?? space.name}
                                        onChange={(e) => setSpaceEdits(prev => ({ ...prev, [space.id]: { ...prev[space.id], name: e.target.value } }))}
                                        className="flex-1 bg-transparent text-content font-bold text-sm outline-none border-b border-border focus:border-border/40 transition-colors py-1"
                                        placeholder="Nom de l'espace"
                                    />
                                    <button
                                        onClick={() => handleSaveSpace(space.id)}
                                        disabled={savingSpaceId === space.id}
                                        className="text-content-muted hover:text-green-400 transition-all disabled:opacity-50"
                                        title="Enregistrer"
                                    >
                                        <Save size={15} />
                                    </button>
                                    <button 
                                        onClick={() => handleDeleteSpace(space.id)}
                                        className="text-gray-600 hover:text-red-500 transition-all"
                                        title="Supprimer"
                                    >
                                        <Trash2 size={15} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                    )}

                    {activeTab === 'portail' && (
                    <div className="bg-surface rounded-2xl p-8 border border-border">
                        <div className="flex items-center justify-between mb-8">
                            <h2 className="text-xl font-bold text-content flex items-center gap-2">
                                <Users size={20} className="text-brand-600 dark:text-brand-400" />
                                Comptes Salariés (Super Admin)
                            </h2>
                        </div>
                        {loadingPortail ? (
                            <div className="text-center py-10"><Clock className="animate-spin text-brand-600 dark:text-brand-400 mx-auto" /></div>
                        ) : employesPortail.length === 0 ? (
                            <p className="py-10 text-center text-content-muted">Aucun salarié disponible sur ce compte.</p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead>
                                        <tr className="text-xs font-bold text-content-muted uppercase tracking-widest border-b border-border">
                                            <th className="pb-4">Salarié</th>
                                            <th className="pb-4">Email</th>
                                            <th className="pb-4">Poste</th>
                                            <th className="pb-4">Statut Portail</th>
                                            <th className="pb-4 text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-white/5">
                                        {employesPortail.map((emp) => (
                                            <tr key={emp.id} className="text-sm text-content-muted">
                                                <td className="py-4 font-semibold text-content">{emp.first_name} {emp.last_name}</td>
                                                <td className="py-4">
                                                    <div>{emp.email || <span className="text-red-400 italic">Pas d'email</span>}</div>
                                                    {emp.portal_username && <div className="text-xs text-brand-600 dark:text-brand-400 font-semibold">Identifiant: {emp.portal_username}</div>}
                                                </td>
                                                <td className="py-4">{emp.position || '—'}</td>
                                                <td className="py-4">
                                                    <span className={`px-2 py-0.5 rounded text-xs font-black ${emp.is_account_active ? 'bg-brand-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
                                                        {emp.is_account_active ? 'ACTIVÉ' : 'BLOQUÉ'}
                                                    </span>
                                                </td>
                                                <td className="py-4 text-right space-x-2">
                                                    <button
                                                        onClick={async () => {
                                                            const res = await apiFetch(`/admin/employes/${emp.id}/block-portal`, {
                                                                method: 'PATCH',
                                                                body: JSON.stringify({ is_blocked: !!emp.is_account_active })
                                                            });
                                                            if (res.success) {
                                                                setEmployesPortail(prev => prev.map(e => e.id === emp.id ? { ...e, is_account_active: emp.is_account_active ? 0 : 1 } : e));
                                                            }
                                                        }}
                                                        className={`text-xs px-2.5 py-1.5 rounded font-bold transition-all ${emp.is_account_active ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20' : 'bg-brand-500/10 text-emerald-400 hover:bg-brand-500/20'}`}
                                                    >
                                                        {emp.is_account_active ? 'Bloquer' : 'Débloquer'}
                                                    </button>
                                                    <button
                                                        onClick={async () => {
                                                            const res = await apiFetch(`/admin/employes/${emp.id}/reset-password`, {
                                                                method: 'PATCH'
                                                            });
                                                            if (res.success) {
                                                                alert('Un nouveau mot de passe a été envoyé par email au salarié.');
                                                            }
                                                        }}
                                                        className="text-xs px-2.5 py-1.5 rounded font-bold bg-surface-soft text-content-muted hover:bg-slate-200 dark:bg-surface-soft transition-all"
                                                    >
                                                        Renvoyer MDP
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                    )}
                </div>
            </div>

            {/* Modal Édition Profil */}
            <AnimatePresence>
                {editMode && (
                    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto transition-colors duration-200">
                        <motion.div 
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="w-full max-w-4xl bg-white dark:bg-[var(--bg-app)] rounded-2xl p-5 md:p-6 shadow-2xl border border-border my-auto transition-colors duration-200"
                        >
                            <h2 className="text-xl font-bold mb-6 text-slate-900 dark:text-content">Éditer la fiche client</h2>
                            <form onSubmit={handleUpdateProfile} className="space-y-6">
                                <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                                    <div className="space-y-5 rounded-2xl border border-border p-4">
                                        <div>
                                            <p className="text-sm font-bold text-slate-900 dark:text-content">Compte client</p>
                                            <p className="text-xs text-slate-500 dark:text-content-muted">Identifiants et marque blanche visibles par le client.</p>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Nom du compte</label>
                                            <input 
                                                type="text" required
                                                value={editData.name}
                                                onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                                                className="w-full px-4 py-3 rounded-lg bg-surface border border-gray-300 dark:border-border text-slate-900 dark:text-content focus:border-slate-400 dark:focus:border-border outline-none transition-colors duration-200"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Email de connexion</label>
                                            <input 
                                                type="email" required
                                                value={editData.email}
                                                onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                                                className="w-full px-4 py-3 rounded-lg bg-surface border border-gray-300 dark:border-border text-slate-900 dark:text-content focus:border-slate-400 dark:focus:border-border outline-none transition-colors duration-200"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Pseudo de connexion</label>
                                            <input 
                                                type="text" required
                                                value={editData.username}
                                                onChange={(e) => setEditData({ ...editData, username: e.target.value.toLowerCase().replace(/\s+/g, '.') })}
                                                className="w-full px-4 py-3 rounded-lg bg-surface border border-gray-300 dark:border-border text-slate-900 dark:text-content focus:border-slate-400 dark:focus:border-border outline-none transition-colors duration-200 font-mono"
                                                placeholder="ex: polpo.hotesse"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Nom marque blanche</label>
                                            <input 
                                                type="text"
                                                value={editData.company_name}
                                                onChange={(e) => setEditData({ ...editData, company_name: e.target.value })}
                                                className="w-full px-4 py-3 rounded-lg bg-surface border border-gray-300 dark:border-border text-slate-900 dark:text-content focus:border-slate-400 dark:focus:border-border outline-none transition-colors duration-200"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Couleur marque blanche (HEX)</label>
                                            <div className="flex gap-2 items-center">
                                                <input 
                                                    type="color"
                                                    value={editData.theme_color || '#987b55'}
                                                    onChange={(e) => setEditData({ ...editData, theme_color: e.target.value })}
                                                    className="w-12 h-12 rounded-lg cursor-pointer bg-transparent border-0 p-0"
                                                />
                                                <input 
                                                    type="text"
                                                    value={editData.theme_color || '#987b55'}
                                                    onChange={(e) => setEditData({ ...editData, theme_color: e.target.value })}
                                                    className="w-full px-4 py-3 rounded-lg bg-surface border border-gray-300 dark:border-border text-slate-900 dark:text-content focus:border-slate-400 dark:focus:border-border outline-none transition-colors duration-200 font-mono"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-5 rounded-2xl border border-cyan-500/20 bg-brand-500/5 p-4">
                                        <div>
                                            <p className="text-sm font-bold text-slate-900 dark:text-content">Contact privé superadmin</p>
                                            <p className="text-xs text-slate-500 dark:text-content-muted">Ces infos sont destinées à votre pilotage interne uniquement.</p>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Prénom</label>
                                                <input type="text" value={editData.account_manager_first_name} onChange={(e) => setEditData({ ...editData, account_manager_first_name: e.target.value })} className="w-full px-4 py-3 rounded-lg bg-surface border border-gray-300 dark:border-border text-slate-900 dark:text-content focus:border-slate-400 dark:focus:border-border outline-none transition-colors duration-200" />
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Nom</label>
                                                <input type="text" value={editData.account_manager_last_name} onChange={(e) => setEditData({ ...editData, account_manager_last_name: e.target.value })} className="w-full px-4 py-3 rounded-lg bg-surface border border-gray-300 dark:border-border text-slate-900 dark:text-content focus:border-slate-400 dark:focus:border-border outline-none transition-colors duration-200" />
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Téléphone</label>
                                                <input type="text" value={editData.account_manager_phone} onChange={(e) => setEditData({ ...editData, account_manager_phone: e.target.value })} className="w-full px-4 py-3 rounded-lg bg-surface border border-gray-300 dark:border-border text-slate-900 dark:text-content focus:border-slate-400 dark:focus:border-border outline-none transition-colors duration-200" />
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Adresse email</label>
                                                <input type="email" value={editData.account_manager_email} onChange={(e) => setEditData({ ...editData, account_manager_email: e.target.value })} className="w-full px-4 py-3 rounded-lg bg-surface border border-gray-300 dark:border-border text-slate-900 dark:text-content focus:border-slate-400 dark:focus:border-border outline-none transition-colors duration-200" />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                                    <div className="space-y-3">
                                        <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Taux de TVA</label>
                                        <div className="flex flex-wrap gap-2 min-h-[32px]">
                                            {editData?.tva_rates?.map((rate) => (
                                                <span key={rate} className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-bold bg-surface-soft dark:bg-surface-soft text-slate-900 dark:text-content border border-slate-300 dark:border-white/20">
                                                    {rate}%
                                                    <button type="button" onClick={() => setEditData({ ...editData, tva_rates: editData.tva_rates.filter(r => r !== rate) })} className="ml-1 text-slate-500 hover:text-red-500 transition-colors font-normal leading-none">×</button>
                                                </span>
                                            ))}
                                            {editData.tva_rates.length === 0 && <span className="text-sm text-slate-400 dark:text-gray-600">Aucun taux configuré</span>}
                                        </div>
                                        <div className="flex gap-2 flex-wrap">
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={editTvaInput}
                                                onChange={(e) => setEditTvaInput(e.target.value)}
                                                placeholder="Ex: 20"
                                                className="w-32 px-3 py-2 rounded-lg bg-surface border border-gray-300 dark:border-border text-slate-900 dark:text-content focus:border-slate-400 dark:focus:border-border outline-none transition-colors duration-200"
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        const val = parseFloat(editTvaInput);
                                                        if (!Number.isFinite(val) || val < 0) return;
                                                        if (editData.tva_rates.includes(val)) return;
                                                        setEditData({ ...editData, tva_rates: [...editData.tva_rates, val].sort((a, b) => a - b) });
                                                        setEditTvaInput('');
                                                    }
                                                }}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const val = parseFloat(editTvaInput);
                                                    if (!Number.isFinite(val) || val < 0) return;
                                                    if (editData.tva_rates.includes(val)) return;
                                                    setEditData({ ...editData, tva_rates: [...editData.tva_rates, val].sort((a, b) => a - b) });
                                                    setEditTvaInput('');
                                                }}
                                                className="px-4 py-2 rounded-lg bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-bold text-sm hover:opacity-80 transition-opacity"
                                            >
                                                + Ajouter
                                            </button>
                                        </div>
                                    </div>

                                    <div className="space-y-3">
                                        <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Champ facture supplémentaire</label>
                                        <button
                                            type="button"
                                            onClick={() => setEditData({ ...editData, enable_cover_count: !editData.enable_cover_count })}
                                            className={`w-full flex items-center justify-between p-4 rounded-xl border transition-all ${
                                                editData.enable_cover_count
                                                    ? 'bg-surface-soft dark:bg-surface-soft border-slate-300 dark:border-white/20 text-slate-900 dark:text-content'
                                                    : 'bg-surface border-gray-300 dark:border-border text-slate-500 dark:text-gray-600'
                                            }`}
                                        >
                                            <span className="font-medium">Activer “Nombre de couverts”</span>
                                            {editData.enable_cover_count ? <ToggleRight size={24} className="text-slate-900 dark:text-content" /> : <ToggleLeft size={24} />}
                                        </button>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Logo Client (.jpg, .png, .svg)</label>
                                    <input 
                                        type="file"
                                        accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                                        onChange={(e) => setEditLogoFile(e.target.files?.[0] || null)}
                                        className="w-full px-4 py-3 rounded-lg bg-surface border border-gray-300 dark:border-border text-slate-900 dark:text-content file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:bg-surface file:text-content dark:file:bg-white dark:file:text-content transition-colors duration-200"
                                    />
                                    {editLogoFile ? (
                                        <p className="text-xs text-content-muted">Nouveau fichier: {editLogoFile.name}</p>
                                    ) : editData.logo_url ? (
                                        <p className="text-xs text-content-muted break-all">Logo actuel: {editData.logo_url}</p>
                                    ) : null}
                                </div>

                                <div className="flex gap-4 pt-4">
                                    <button 
                                        type="button"
                                        onClick={() => setEditMode(false)}
                                        className="flex-1 px-6 py-4 rounded-lg font-bold border border-gray-300 dark:border-border text-slate-900 dark:text-content hover:bg-slate-100 dark:hover:bg-surface-soft transition-colors duration-200"
                                    >
                                        Annuler
                                    </button>
                                    <button 
                                        type="submit"
                                        className="flex-1 bg-white text-slate-900 px-6 py-4 rounded-lg font-bold hover:bg-gray-200 transition-all"
                                    >
                                        Enregistrer
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Modal Collaborateur */}
            <style>{`
                .collab-modal-scrollbar::-webkit-scrollbar {
                    width: 6px;
                }
                .collab-modal-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .collab-modal-scrollbar::-webkit-scrollbar-thumb {
                    background: rgba(255, 255, 255, 0.15);
                    border-radius: 3px;
                }
                .collab-modal-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: rgba(255, 255, 255, 0.25);
                }
                .collab-modal-scrollbar {
                    scrollbar-width: thin;
                    scrollbar-color: rgba(255, 255, 255, 0.15) transparent;
                }
            `}</style>
            <AnimatePresence>
                {showCollabModal && (
                    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 md:p-6 overflow-y-auto transition-colors duration-200">
                        <motion.div 
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="w-full max-w-md bg-white dark:bg-[var(--bg-app)] rounded-2xl shadow-2xl border border-border my-auto flex flex-col max-h-[90vh] transition-colors duration-200"
                        >
                            {/* Contenu défilable */}
                            <div className="overflow-y-auto collab-modal-scrollbar flex-1">
                                <div className="p-8">
                                    <h2 className="text-2xl font-bold mb-6 text-slate-900 dark:text-content">{editingCollaboratorId ? 'Modifier Collaborateur' : 'Nouveau Collaborateur'}</h2>
                                    <form id="collab-form" onSubmit={handleAddCollab} className="space-y-4">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Nom complet</label>
                                    <input 
                                        type="text" required
                                        value={newCollab.name}
                                        onChange={(e) => setNewCollab({ ...newCollab, name: e.target.value })}
                                        className="w-full px-4 py-3 rounded-lg bg-surface border border-gray-300 dark:border-border text-slate-900 dark:text-content focus:border-slate-400 dark:focus:border-border outline-none transition-colors duration-200"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-content-muted uppercase tracking-widest">
                                        Pseudo de connexion <span className="text-red-400">*</span>
                                    </label>
                                    <input 
                                        type="text" required
                                        value={newCollab.username}
                                        onChange={(e) => setNewCollab({ ...newCollab, username: e.target.value.toLowerCase().replace(/\s+/g, '.') })}
                                        className="w-full px-4 py-3 rounded-lg bg-surface border border-gray-300 dark:border-border text-slate-900 dark:text-content focus:border-slate-400 dark:focus:border-border outline-none transition-colors duration-200 font-mono"
                                        placeholder="ex: polpo.hotesse"
                                        autoComplete="off"
                                    />
                                    <p className="text-[11px] text-gray-600">Utilisé pour se connecter à la place de l'email.</p>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Email <span className="text-gray-600 font-normal normal-case">(optionnel)</span>
                                    </label>
                                    <input 
                                        type="email"
                                        value={newCollab.email}
                                        onChange={(e) => setNewCollab({ ...newCollab, email: e.target.value })}
                                        className="w-full px-4 py-3 rounded-lg bg-surface border border-gray-300 dark:border-border text-slate-900 dark:text-content focus:border-slate-400 dark:focus:border-border outline-none transition-colors duration-200"
                                        placeholder="nom@exemple.com"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Rôle</label>
                                    <input 
                                        type="text"
                                        value={newCollab.role}
                                        onChange={(e) => setNewCollab({ ...newCollab, role: e.target.value })}
                                        className="w-full px-4 py-3 rounded-lg bg-surface border border-gray-300 dark:border-border text-slate-900 dark:text-content focus:border-slate-400 dark:focus:border-border outline-none transition-colors duration-200"
                                        placeholder="ex: Manager, Hôtesse..."
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-content-muted uppercase tracking-widest">
                                        Employé rattaché <span className="text-gray-600 font-normal normal-case">(optionnel)</span>
                                    </label>
                                    <select
                                        value={newCollab.employe_id || ''}
                                        onChange={(e) => setNewCollab({ ...newCollab, employe_id: e.target.value || null })}
                                        disabled={loadingPortail}
                                        className="w-full px-4 py-3 rounded-lg bg-surface border border-gray-300 dark:border-border text-slate-900 dark:text-content focus:border-slate-400 dark:focus:border-border outline-none transition-colors duration-200"
                                    >
                                        <option value="">— Aucun (compte générique) —</option>
                                        {employesPortail
                                            .filter((emp: any) => {
                                                const takenBy = collaborators.find((c: any) => c.employe_id === emp.id);
                                                return !takenBy || takenBy.id === editingCollaboratorId;
                                            })
                                            .map((emp: any) => (
                                                <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</option>
                                            ))}
                                    </select>
                                    <p className="text-[11px] text-gray-600">La personne qui se connecte réellement avec ce compte — utilisé pour afficher son vrai nom lors d'un partage (ex: module ToDo).</p>
                                </div>
                                {!editingCollaboratorId && (
                                    <div className="space-y-1.5 px-3 py-2.5 rounded-lg bg-brand-500/10 border border-brand-500/20">
                                        <p className="text-[11px] text-content-muted">
                                            Un mot de passe temporaire sera généré automatiquement et envoyé au collaborateur par email. Il devra le modifier à sa première connexion.
                                        </p>
                                    </div>
                                )}
                                <div className="space-y-3 pt-2">
                                    <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Accès Modules</label>
                                    <div className="grid grid-cols-2 gap-3">
                                        {getModulesForClientType(client?.client_type).map((m) => {
                                            const checked = newCollab.modules_access.includes(m.name);
                                            return (
                                                <label key={m.name} className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer transition-all ${checked ? 'border-slate-300 dark:border-white/30 bg-surface-soft dark:bg-surface-soft text-slate-900 dark:text-content' : 'border-gray-300 dark:border-border text-slate-500 dark:text-content-muted hover:text-slate-900 dark:hover:text-slate-900'}`}>
                                                    <input
                                                        type="checkbox"
                                                        checked={checked}
                                                        onChange={() => toggleCollaboratorModule(m.name)}
                                                        className="accent-white"
                                                    />
                                                    <span className="text-sm font-medium">{m.label}</span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            </form>
                                </div>
                            </div>

                            {/* Footer sticky avec boutons */}
                            <div className="sticky bottom-0 border-t border-border bg-white dark:bg-[var(--bg-app)] p-6 flex gap-4 transition-colors duration-200">
                                <button 
                                    type="button"
                                    onClick={() => {
                                        setShowCollabModal(false);
                                        setEditingCollaboratorId(null);
                                    }}
                                    className="flex-1 px-6 py-3 rounded-lg font-bold border border-gray-300 dark:border-border text-slate-900 dark:text-content hover:bg-slate-100 dark:hover:bg-surface-soft transition-colors duration-200"
                                >
                                    Annuler
                                </button>
                                <button 
                                    type="submit"
                                    form="collab-form"
                                    className="flex-1 bg-white text-slate-900 px-6 py-3 rounded-lg font-bold hover:bg-gray-200 transition-all"
                                >
                                    {editingCollaboratorId ? 'Enregistrer' : 'Créer'}
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Modal Espace */}
            <AnimatePresence>
                {showSpaceModal && (
                    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6 transition-colors duration-200">
                        <motion.div 
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="w-full max-w-md bg-white dark:bg-[var(--bg-app)] rounded-2xl p-10 shadow-2xl border border-border transition-colors duration-200"
                        >
                            <h2 className="text-2xl font-bold mb-8 text-slate-900 dark:text-content">Nouvel Espace</h2>
                            <form onSubmit={handleAddSpace} className="space-y-6">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Nom de l'espace</label>
                                    <input 
                                        type="text" required
                                        value={newSpace.name}
                                        onChange={(e) => setNewSpace({ ...newSpace, name: e.target.value })}
                                        className="w-full px-4 py-3 rounded-lg bg-surface border border-gray-300 dark:border-border text-slate-900 dark:text-content focus:border-slate-400 dark:focus:border-border outline-none transition-colors duration-200"
                                        placeholder="ex: RDC, Salon VIP..."
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Couleur sur le calendrier</label>
                                    <div className="flex gap-3">
                                        <input 
                                            type="color"
                                            value={newSpace.color}
                                            onChange={(e) => setNewSpace({ ...newSpace, color: e.target.value })}
                                            className="w-12 h-12 rounded bg-surface border border-gray-300 dark:border-border cursor-pointer transition-colors duration-200"
                                        />
                                        <input 
                                            type="text"
                                            value={newSpace.color}
                                            onChange={(e) => setNewSpace({ ...newSpace, color: e.target.value })}
                                            className="flex-1 px-4 py-3 rounded-lg bg-surface border border-gray-300 dark:border-border text-slate-900 dark:text-content focus:border-slate-400 dark:focus:border-border outline-none transition-colors duration-200 font-mono"
                                        />
                                    </div>
                                </div>
                                <div className="flex gap-4 pt-4">
                                    <button 
                                        type="button"
                                        onClick={() => setShowSpaceModal(false)}
                                        className="flex-1 px-6 py-4 rounded-lg font-bold border border-gray-300 dark:border-border text-slate-900 dark:text-content hover:bg-slate-100 dark:hover:bg-surface-soft transition-colors duration-200"
                                    >
                                        Annuler
                                    </button>
                                    <button 
                                        type="submit"
                                        className="flex-1 bg-white text-slate-900 px-6 py-4 rounded-lg font-bold hover:bg-gray-200 transition-all"
                                    >
                                        Ajouter
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Modal Import Excel */}
            <AnimatePresence>
                {showImportModal && isSuperAdmin && (
                    <div className="fixed inset-0 bg-black/90 backdrop-blur-md z-50 flex items-center justify-center p-6 transition-colors duration-200">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="w-full max-w-xl max-h-[85vh] bg-white dark:bg-[var(--bg-app)] rounded-3xl border border-border shadow-2xl overflow-hidden flex flex-col transition-colors duration-200"
                        >
                            <div className="p-6 border-b border-border flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 bg-green-500/10 rounded-xl flex items-center justify-center text-green-400">
                                        <FileText size={20} />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-content">Import Excel — {client?.company_name}</h3>
                                        <p className="text-xs text-content-muted">Employés & Postes en masse</p>
                                    </div>
                                </div>
                                <button onClick={handleCloseImportModal} className="p-2 hover:bg-surface-soft rounded-xl text-content-muted transition-all">
                                    <X size={20} />
                                </button>
                            </div>

                            <div className="flex-1 overflow-y-auto p-6">
                                {!importResult && (
                                    <div className="space-y-6">
                                        {!importPreview && (
                                            <div>
                                                <p className="text-xs text-content-muted mb-3 font-bold uppercase tracking-widest">Format attendu</p>
                                                <div className="bg-white/[0.03] rounded-xl border border-border p-4 text-xs text-content-muted font-mono mb-4">
                                                    <span className="text-content font-bold">Nom</span> · <span className="text-green-400">Prénom</span> · <span className="text-brand-600 dark:text-brand-400">Poste</span> · Email · Téléphone
                                                    <br />
                                                    <span className="text-[10px] text-content-muted">(Email et Téléphone optionnels)</span>
                                                </div>
                                                <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-border rounded-2xl cursor-pointer hover:border-brand-500/40 hover:bg-brand-500/5 transition-all">
                                                    <Upload size={32} className="text-content-muted mb-3" />
                                                    <span className="text-sm text-content-muted font-medium">
                                                        Cliquer pour sélectionner un fichier Excel
                                                    </span>
                                                    <span className="text-[10px] text-content-muted mt-1">.xlsx, .xls, .csv</span>
                                                    <input 
                                                        type="file" 
                                                        accept=".xlsx,.xls,.csv" 
                                                        className="hidden" 
                                                        onChange={handleImportFileChange} 
                                                    />
                                                </label>
                                            </div>
                                        )}

                                        {importLoading && (
                                            <div className="flex items-center justify-center gap-3 py-6 text-content-muted">
                                                <div className="w-5 h-5 border-2 border-white/20 border-t-green-400 rounded-full animate-spin" />
                                                <span className="text-sm">{importPreview ? 'Importation en cours...' : 'Analyse du fichier...'}</span>
                                            </div>
                                        )}

                                        {importError && (
                                            <div className="flex items-start gap-3 bg-red-500/10 border border-red-500/20 rounded-xl p-4">
                                                <AlertCircle size={18} className="text-red-400 flex-shrink-0 mt-0.5" />
                                                <p className="text-sm text-red-400">{importError}</p>
                                            </div>
                                        )}

                                        {importPreview && !importLoading && (
                                            <div className="space-y-4">
                                                <div className="bg-white/[0.03] rounded-2xl border border-border p-5 space-y-3">
                                                    <p className="text-xs font-bold text-content-muted uppercase tracking-widest">Analyse Flash</p>
                                                    <div className="grid grid-cols-3 gap-4">
                                                        <div className="text-center">
                                                            <p className="text-2xl font-bold text-content">{importPreview.valid_employees}</p>
                                                            <p className="text-xs text-content-muted mt-1">Employés</p>
                                                        </div>
                                                        <div className="text-center">
                                                            <p className="text-2xl font-bold text-green-400">{importPreview.new_posts?.length ?? 0}</p>
                                                            <p className="text-xs text-content-muted mt-1">Nouveaux postes</p>
                                                        </div>
                                                        <div className="text-center">
                                                            <p className="text-2xl font-bold text-orange-400">{importPreview.errors?.length ?? 0}</p>
                                                            <p className="text-xs text-content-muted mt-1">Avertissements</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {importResult && (
                                    <div className="py-12 flex flex-col items-center text-center space-y-6">
                                        <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center">
                                            <CheckCircle2 size={40} className="text-green-400" />
                                        </div>
                                        <h4 className="text-2xl font-bold text-content">Importation Terminée !</h4>
                                    </div>
                                )}
                            </div>

                            <div className="p-5 bg-[var(--bg-app)] border-t border-border flex gap-3 sticky bottom-0">
                                <button onClick={handleCloseImportModal} className="flex-1 px-4 py-3 rounded-xl font-bold border border-border text-content hover:bg-surface-soft transition-all text-sm">
                                    {importResult ? 'Fermer' : 'Annuler'}
                                </button>
                                {importPreview && !importLoading && !importResult && (
                                    <button
                                        onClick={handleExecuteImport}
                                        className="flex-1 px-4 py-3 bg-green-500 text-content rounded-xl font-bold hover:bg-green-600 transition-all text-sm flex items-center justify-center gap-2"
                                    >
                                        <Upload size={15} />
                                        Lancer l'intégration
                                    </button>
                                )}
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Modal Import Événementiel */}
            <AnimatePresence>
                {showEvenementielImportModal && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={handleCloseEvenementielImportModal} className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
                        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="relative bg-surface border border-border rounded-3xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
                        <div className="p-6 border-b border-border flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-brand-500/10 rounded-xl">
                                    <Calendar size={20} className="text-brand-600 dark:text-brand-400" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-content">Import Événementiel</h3>
                                    <p className="text-xs text-content-muted">Importer des privatisations, clients CRM et staff</p>
                                </div>
                            </div>
                            <button onClick={handleCloseEvenementielImportModal} className="p-2 hover:bg-surface-soft rounded-xl text-content-muted transition-all">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6">
                            {!evenementielImportResult && (
                                <div className="space-y-6">
                                    {!evenementielImportPreview && (
                                        <div>
                                            <p className="text-xs text-content-muted mb-3 font-bold uppercase tracking-widest">Colonnes attendues</p>
                                            <div className="bg-white/[0.03] rounded-xl border border-border p-4 text-[10px] text-content-muted font-mono mb-4 grid grid-cols-2 gap-x-4 gap-y-1">
                                                <span><span className="text-brand-600 dark:text-brand-400">Mois/Année</span> : ex: 05/2026</span>
                                                <span><span className="text-brand-600 dark:text-brand-400">Client</span> : Nom de l'entreprise</span>
                                                <span><span className="text-brand-600 dark:text-brand-400">Date</span> : ex: 15/05/2026</span>
                                                <span><span className="text-brand-600 dark:text-brand-400">Heure Début</span> : ex: 19h</span>
                                                <span><span className="text-brand-600 dark:text-brand-400">Heure Fin</span> : ex: 02h</span>
                                                <span><span className="text-brand-600 dark:text-brand-400">Espace</span> : ex: Salle</span>
                                                <span><span className="text-content-muted">Staff</span> : Nom Prénom (séparés par ;)</span>
                                                <span><span className="text-content-muted">Nb Pers.</span> : ex: 50</span>
                                            </div>
                                            <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-border rounded-2xl cursor-pointer hover:border-brand-500/40 hover:bg-brand-500/5 transition-all">
                                                <Upload size={32} className="text-content-muted mb-3" />
                                                <span className="text-sm text-content-muted font-medium">
                                                    {evenementielImportFile ? evenementielImportFile.name : 'Sélectionner un fichier Excel (.xlsx, .csv)'}
                                                </span>
                                                <input
                                                    type="file"
                                                    accept=".xlsx,.xls,.csv"
                                                    className="hidden"
                                                    onChange={handleEvenementielImportFileChange}
                                                />
                                            </label>
                                        </div>
                                    )}

                                    {evenementielImportLoading && (
                                        <div className="flex flex-col items-center justify-center py-12 text-content-muted gap-3">
                                            <div className="w-8 h-8 border-3 border-border border-t-blue-400 rounded-full animate-spin" />
                                            <p className="text-sm">Traitement en cours...</p>
                                        </div>
                                    )}

                                    {evenementielImportError && (
                                        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl flex items-start gap-3">
                                            <AlertCircle size={20} className="text-red-400 flex-shrink-0" />
                                            <p className="text-sm text-red-400">{evenementielImportError}</p>
                                        </div>
                                    )}

                                    {evenementielImportPreview && !evenementielImportLoading && (
                                        <div className="space-y-4">
                                            <div className="grid grid-cols-3 gap-4">
                                                <div className="bg-surface-soft rounded-2xl p-4 border border-border">
                                                    <p className="text-2xl font-bold text-content">{evenementielImportPreview.total}</p>
                                                    <p className="text-[10px] text-content-muted uppercase tracking-widest font-bold">Total détecté</p>
                                                </div>
                                                <div className="bg-green-500/5 rounded-2xl p-4 border border-green-500/10">
                                                    <p className="text-2xl font-bold text-green-400">{evenementielImportPreview.valid}</p>
                                                    <p className="text-[10px] text-content-muted uppercase tracking-widest font-bold">Prêts à l'import</p>
                                                </div>
                                                <div className="bg-red-500/5 rounded-2xl p-4 border border-red-500/10">
                                                    <p className="text-2xl font-bold text-red-400">{evenementielImportPreview.errors}</p>
                                                    <p className="text-[10px] text-content-muted uppercase tracking-widest font-bold">Incomplets</p>
                                                </div>
                                            </div>

                                            <div className="border border-border rounded-2xl overflow-hidden overflow-x-auto">
                                                <table className="w-full text-[11px] text-left">
                                                    <thead className="bg-surface-soft text-content-muted uppercase tracking-widest text-[9px]">
                                                        <tr>
                                                            <th className="px-4 py-3">Date</th>
                                                            <th className="px-4 py-3">Client</th>
                                                            <th className="px-4 py-3">Horaires</th>
                                                            <th className="px-4 py-3">Espace</th>
                                                            <th className="px-4 py-3">Staff</th>
                                                            <th className="px-4 py-3">Statut</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-white/5">
                                                        {evenementielImportPreview.rows.map((r: any, i: number) => (
                                                            <tr key={i} className="text-content-muted">
                                                                <td className="px-4 py-3">{r.date}</td>
                                                                <td className="px-4 py-3 font-bold">{r.client}</td>
                                                                <td className="px-4 py-3">{r.start} - {r.end}</td>
                                                                <td className="px-4 py-3">{r.espace || '-'}</td>
                                                                <td className="px-4 py-3">
                                                                    <div className="flex flex-wrap gap-1">
                                                                        {Array.isArray(r.staffs) && r.staffs.map((s: any, si: number) => (
                                                                            <span key={si} className={`px-1.5 py-0.5 rounded ${s?.id ? 'bg-green-500/10 text-green-400' : 'bg-gray-500/10 text-content-muted'}`}>
                                                                                {s?.name || 'Inconnu'}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                </td>
                                                                <td className="px-4 py-3">
                                                                    {r.isValid ? (
                                                                        <span className="text-green-400 font-bold">OK</span>
                                                                    ) : (
                                                                        <span className="text-red-400 font-bold">Erreur</span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {evenementielImportResult && (
                                <div className="py-12 flex flex-col items-center text-center space-y-6">
                                    <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center">
                                        <CheckCircle2 size={40} className="text-green-400" />
                                    </div>
                                    <div>
                                        <h4 className="text-2xl font-bold text-content">Importation Terminée !</h4>
                                        <p className="text-content-muted mt-2">
                                            {evenementielImportResult.createdCount} événement(s) créé(s) avec succès.<br />
                                            {evenementielImportResult.crmCreated} contact(s) CRM ajouté(s).
                                        </p>
                                    </div>
                                    <button onClick={handleCloseEvenementielImportModal} className="px-8 py-3 bg-white text-slate-900 font-bold rounded-xl hover:bg-gray-200 transition-all">
                                        Fermer
                                    </button>
                                </div>
                            )}
                        </div>

                        {!evenementielImportResult && (
                            <div className="p-6 border-t border-border bg-white/[0.02] flex gap-3">
                                <button onClick={handleCloseEvenementielImportModal} className="flex-1 py-3 px-4 border border-border rounded-xl font-bold text-content hover:bg-surface-soft transition-all">
                                    Annuler
                                </button>
                                {evenementielImportPreview && !evenementielImportLoading && (
                                    <button
                                        onClick={executeEvenementielImport}
                                        disabled={evenementielImportPreview.valid === 0}
                                        className="flex-[2] py-3 px-4 bg-brand-600 text-brand-fg rounded-xl font-bold hover:bg-brand-500 transition-all disabled:opacity-50"
                                    >
                                        Lancer l'importation ({evenementielImportPreview.valid} lignes)
                                    </button>
                                )}
                            </div>
                        )}
                    </motion.div>
                </div>
            )}
        </AnimatePresence>

    </div>
);
}

// Page d'accueil publique (déconnecté) ou tableau de bord (connecté) sur la même route "/" — condition
// nécessaire pour que l-iamani.com serve à la fois l'accueil marketing et l'espace connecté sous un seul
// domaine. Placée en dehors du "/*" + <Layout> habituel : la page d'accueil a son propre habillage complet
// (header/footer marketing), pas la sidebar/chrome de l'application.
const RootRoute = () => {
    const { user, loading } = useAuth();
    if (loading) return null;
    if (user) return <Layout><DashboardView /></Layout>;
    return <PublicHomepage />;
};

// Le favicon de l'onglet du navigateur était figé sur /favicon.png (fichier statique dans index.html),
// jamais relié au logo configuré par le super admin dans Paramètres (loginLogo, déjà utilisé pour le
// logo affiché sur la page de connexion et l'accueil) — corrigé en le mettant à jour dynamiquement au
// chargement de l'app, quelle que soit la page d'entrée (connexion, accueil, tableau de bord...).
const DynamicFavicon = () => {
    React.useEffect(() => {
        adminApi.getGlobalSettings().then((data: any) => {
            if (!data?.loginLogo) return;
            const img = new Image();
            img.onload = () => {
                // Le logo configuré est un rectangle large (logo texte), pas un carré — utilisé tel quel,
                // le navigateur l'écrase dans la case carrée du favicon et le déforme. On le redessine donc
                // "contain" (proportions conservées, centré) sur un canevas carré avant de s'en servir.
                const size = 64;
                const canvas = document.createElement('canvas');
                canvas.width = size;
                canvas.height = size;
                const ctx = canvas.getContext('2d');
                if (!ctx) return;
                const scale = Math.min(size / img.width, size / img.height);
                const w = img.width * scale;
                const h = img.height * scale;
                ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);

                let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
                if (!link) {
                    link = document.createElement('link');
                    link.rel = 'icon';
                    document.head.appendChild(link);
                }
                link.href = canvas.toDataURL('image/png');
            };
            img.src = data.loginLogo;
        }).catch(() => {});
    }, []);
    return null;
};

const App = () => (
    <Router>
        <AuthProvider>
            <DynamicFavicon />
            {/* Numero de page, uniquement hors production — voir src/lib/pageRegistry.ts */}
            <PageNumberBadge />
            <Routes>
                <Route path="/" element={<RootRoute />} />
                <Route path="/login" element={<LoginView />} />
                <Route path="/inscription/:token" element={<ClientRegistrationForm />} />
                <Route path="/reserver/:clientId" element={<BookingWidget />} />
                <Route path="/rdv-pro-reserver/:clientId" element={<RdvProBookingWidget />} />
                <Route path="/pointage-kiosque/:clientId" element={<PointageKiosque />} />
                <Route path="/documentation/:token" element={<DocumentationResponse />} />
                <Route path="/mon-repas/:token" element={<EvenementielGuestPage />} />
                <Route path="/ma-table/:publicToken/:tableNumber" element={<TableCheckinPage />} />
                <Route path="/commande-fournisseur/:token" element={<SupplierOrderConfirmPage />} />
                <Route path="/annulation-rdv/:token" element={<RdvCancellationPage />} />
                <Route path="/extras-demande/:token" element={<ExtrasRequestConfirmPage />} />
                <Route path="/devis/:token" element={<DevisSignaturePage />} />
                <Route element={<LegalLayout />}>
                    <Route path="/legal" element={<LegalMentions />} />
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                </Route>
                <Route path="/*" element={
                    <Layout>
                        <Routes>
                            <Route path="/demandes-recurrentes" element={<DemandesRecurrentesPage />} />
                            <Route path="/crm/contacts/:id/demarches" element={<CRMContactDemarchesPage />} />
                            <Route path="/crm/contacts/:id/factures" element={<CRMContactFacturesPage />} />
                            <Route path="/crm/contacts/:id/commandes" element={<CRMContactCommandesPage />} />
                            <Route path="/crm/contacts/:id/rendez-vous" element={<CRMContactHistoriquePage />} />
                            <Route path="/crm/contacts/:id/evenements" element={<CRMContactHistoriquePage />} />
                            <Route path="/mon-tableau-de-bord" element={<PrestationEmployeeDashboard />} />
                            <Route path="/planning" element={<PlanningModule />} />
                            <Route path="/feuille-de-route" element={<RoadmapPage />} />
                            <Route path="/absences" element={<EmployeeAbsences />} />
                            <Route path="/mes-sondages" element={<EmployeeSurveys />} />
                            <Route path="/mon-integration" element={<EmployeeOnboarding />} />
                            <Route path="/mes-notes-de-frais" element={<EmployeeExpenses />} />
                            <Route path="/pointage" element={<EmployeeTimeClock />} />
                            <Route path="/mes-messages" element={<EmployeeMessages />} />
                            <Route path="/crm" element={<CRMModule />} />
                            <Route path="/todo" element={<TodoModule />} />
                            <Route path="/notes" element={<NotesModule />} />
                            <Route path="/crm/contacts/:id" element={<CRMModule />} />
                            <Route path="/fournisseurs" element={<FournisseursModule />} />
                            <Route path="/fournisseurs/:id" element={<SupplierDetailPage />} />
                            <Route path="/fournisseurs/:id/commandes" element={<SupplierOrdersPage />} />
                            <Route path="/fournisseurs/:id/articles" element={<SupplierArticlesPage />} />
                            <Route path="/fournisseurs/:id/articles/:categoryId" element={<SupplierCategoryArticlesPage />} />
                            <Route path="/extras-partenaires" element={<ExtrasPartnersModule />} />
                            <Route path="/extras-partenaires/:id" element={<ExtrasPartnerDetailPage />} />
                            <Route path="/extras-partenaires/:id/types" element={<ExtrasPartnerTypesPage />} />
                            <Route path="/extras-partenaires/:id/types/:categoryId" element={<ExtrasPartnerCategoryTypesPage />} />
                            <Route path="/extras-partenaires/:id/demandes" element={<ExtrasPartnerRequestsPage />} />
                            <Route path="/extras" element={<ExtrasModule />} />
                            <Route path="/commandes" element={<CommandesModule />} />
                            <Route path="/factures" element={<FacturesModule />} />
                            <Route path="/employes" element={<PostesEmployesModule />} />
                            <Route path="/employes/:id" element={<EmployeeProfilePage />} />
                            <Route path="/demandes-conges" element={<RHModule />} />
                            <Route path="/messagerie" element={<TeamMessagingPage />} />
                            <Route path="/notes-de-frais" element={<NotesDeFraisPage />} />
                            <Route path="/pointage-admin" element={<PointageManagement />} />
                            <Route path="/exception-service" element={<ExceptionServiceModule />} />
                            <Route path="/prestations" element={<PrestationsModule />} />
                            <Route path="/planning-rdv" element={<PrestationPlanningModule />} />
                            <Route path="/parametres" element={<SettingsPage />} />
                            <Route path="/admin/sentinel" element={<SentinelJournal />} />
                            <Route path="/admin/settings" element={<AdminSettings />} />
                            <Route path="/admin/collaborator-requests" element={<AdminCollaboratorRequests />} />
                            <Route path="/admin/exception-service-automation-requests" element={window.location.hostname === 'gestion.l-iamani.com' ? <Navigate to="/" replace /> : <AdminExceptionServiceAutomationRequests />} />
                            <Route path="/admin/clients" element={<AdminClientsView />} />
                            <Route path="/admin/registration-requests" element={<RegistrationRequestsView />} />
                            <Route path="/admin/clients/:id" element={<AdminClientDetailView />} />
                            <Route path="/admin/support" element={<SupportAdminView />} />
                            <Route path="/evenementiel" element={<EvenementielModule />} />
                            <Route path="/menu-plats" element={<MenuCatalogPage />} />
                            <Route path="/menu-plats/allergenes" element={<AllergenFamiliesPage />} />
                            <Route path="/suivi-privatisation" element={<SuiviPrivatisationModule />} />
                            <Route path="/suivi-privatisation/:dossierId" element={<SuiviPrivatisationLayout />}>
                                <Route index element={<Navigate to="pipeline" replace />} />
                                <Route path="pipeline" element={<DossierPipelinePage />} />
                                <Route path="brief" element={<DossierBriefPage />} />
                                <Route path="documents" element={<DossierDocumentsPage />} />
                                <Route path="menus" element={<DossierMenusPage />} />
                                <Route path="journal" element={<DossierJournalPage />} />
                            </Route>
                            <Route path="/menus-convives" element={<MenusConvivesListPage />} />
                            <Route path="/menus-convives/:eventId" element={<ConvivesSuiviPage />} />
                            <Route path="/menus-convives/:eventId/menu" element={<EvenementielConvivesPage />} />
                            <Route path="/mode-terrain" element={<ModeTerrainListPage />} />
                            <Route path="/mode-terrain/:eventId" element={<ModeTerrainPage />} />
                            <Route path="/menus-convives/:eventId/qr-codes" element={<QrCodesPage />} />
                            <Route path="/rdv-pro" element={<RdvProModule />} />
                            <Route path="/rdv-pro/annulations" element={<RdvCancellationHistoryPage />} />
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                    </Layout>
                } />
            </Routes>
        </AuthProvider>
    </Router>
);

export default App;
